import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export type Locale = 'fr' | 'en' | 'nl' | 'pl' | 'ro';

const translations = {
  en: {
    // Hero
    heroTag: "Premium Device Skins",
    heroPhoneTitle1: "Protect your",
    heroPhoneTitle2: "phone in style.",
    heroPhoneDesc: "Choose between Hydrogel or 3M Leather skins for iPhones & Samsung. Two premium materials, one perfect fit.",
    heroCameraTitle1: "Elevate your",
    heroCameraTitle2: "camera game.",
    heroCameraDesc: "Hydrogel or 3M Leather skins for Canon & Nikon. Shield your camera body with a look that stands out.",
    heroMacbookTitle1: "Transform your",
    heroMacbookTitle2: "MacBook look.",
    heroMacbookDesc: "Hydrogel or 3M Leather skins for MacBook Pro & Air. Turn your laptop into a statement piece.",
    heroControllerTitle1: "Level up your",
    heroControllerTitle2: "controller style.",
    heroControllerDesc: "Hydrogel skins for PS5 DualSense controllers. 6 exclusive designs to match your gaming setup.",
    heroTelephoneTitle1: "Style for your",
    heroTelephoneTitle2: "telephone.",
    heroTelephoneDesc: "Hydrogel and 3M Leather skins for the latest iPhone, Samsung and more.",
    heroComputerTitle1: "Upgrade your",
    heroComputerTitle2: "computer.",
    heroComputerDesc: "3M Vinyl skins for MacBook, Dell, Lenovo and other popular laptops.",
    heroConsoleTitle1: "Customise your",
    heroConsoleTitle2: "console.",
    heroConsoleDesc: "3M Vinyl skins for PS5, PS4, Xbox and Nintendo Switch.",
    heroCameraPhotoTitle1: "Protect your",
    heroCameraPhotoTitle2: "camera.",
    heroCameraPhotoDesc: "3M Vinyl skins for Canon, Nikon and Sony cameras & lenses.",
    telephones: "Telephones",
    computers: "Computers",
    consoles: "Consoles",
    cameraPhoto: "Camera photo",
    shopNow: "Shop Now",
    dragToCompare: "← Drag to compare →",
    before: "Before",
    withSkin: "With Skin",
    // Two Types Section
    twoTypesTag: "Two Premium Materials",
    twoTypesTitle1: "Choose Your",
    twoTypesTitle2: "Material",
    twoTypesDesc: "Every device deserves the perfect skin. Choose between our two premium materials, each engineered for a different experience.",
    // Hydrogel
    hydrogel: "Hydrogel",
    hydrogelTag: "Ultra-Thin Protection",
    hydrogelDesc: "A self-healing, ultra-flexible film that wraps around edges seamlessly. Crystal clear or printed designs with anti-fingerprint coating.",
    hydrogelFeature1: "Self-healing technology",
    hydrogelFeature2: "0.15mm ultra-thin",
    hydrogelFeature3: "Anti-fingerprint coating",
    hydrogelFeature4: "Flexible edge wrapping",
    hydrogelPrice: "From €14.99",
    // 3M Leather
    leather3m: "3M Leather",
    leather3mTag: "Premium Texture",
    leather3mDesc: "Genuine 3M textured vinyl with rich leather grain. A luxurious tactile feel that transforms your device into a statement piece.",
    leather3mFeature1: "Genuine 3M vinyl",
    leather3mFeature2: "Rich leather texture",
    leather3mFeature3: "UV & scratch resistant",
    leather3mFeature4: "Precision laser-cut",
    leather3mPrice: "From €19.99",
    // Comparison
    comparisonTitle: "Side by Side",
    compThickness: "Thickness",
    compThicknessHydro: "0.15mm",
    compThicknessLeather: "0.25mm",
    compTexture: "Texture",
    compTextureHydro: "Smooth / Glossy",
    compTextureLeather: "Rich Leather Grain",
    compProtection: "Protection",
    compProtectionHydro: "Self-healing film",
    compProtectionLeather: "3M scratch shield",
    compDurability: "Durability",
    compDurabilityHydro: "2–3 years",
    compDurabilityLeather: "3–5 years",
    compApplication: "Application",
    compApplicationHydro: "Wet apply, no bubbles",
    compApplicationLeather: "Dry apply, air release",
    compBestFor: "Best For",
    compBestForHydro: "Sleek, minimal look",
    compBestForLeather: "Luxury, tactile feel",
    exploreSkins: "Explore Skins",
    // Features
    feature1Title: "Two Premium Materials",
    feature1Desc: "Choose Hydrogel for ultra-thin clarity or 3M Leather for luxurious texture.",
    feature2Title: "Zero Bulk",
    feature2Desc: "Ultra-thin skins that add zero thickness to your device.",
    feature3Title: "Bubble-Free Application",
    feature3Desc: "Air-release technology for a flawless, professional finish every time.",
    // How It Works
    howItWorks: "How It Works",
    howItWorksDesc: "Three simple steps to protect your gear.",
    step1Title: "Choose Your Device",
    step1Desc: "Select your device brand and model from our extensive catalog.",
    step2Title: "Pick Your Material",
    step2Desc: "Choose between Hydrogel or 3M Leather, then pick your design.",
    step3Title: "Apply & Enjoy",
    step3Desc: "Easy bubble-free application in just a few minutes.",
    // Categories
    cameras: "Cameras",
    iphones: "Phones",
    macbooks: "MacBooks",
    controllers: "Controllers",
    accessories: "Accessories",
    cardSkins: "Card skins",
    laptops: "Laptops",
    tablets: "Tablets",
    all: "All",
    allProducts: "All Products",
    allModels: "All models",
    shop: "Shop",
    discover: "Discover",
    help: "Help & Account",
    quizTitle: "Style Matchmaker",
    tutorials: "Application Guide",
    returns: "Returns & Refunds",
    skinsSuffix: "Skins",
    comingSoon: "Coming soon",
    comingSoonDesc: "This collection is launching soon. Check back shortly — we're crafting something special.",
    categoryHeroDesc: "Hydrogel and 3M Leather skins, custom-cut for every model.",
    noProducts: "No products found",
    noProductsHint: "New products are being prepared for this category. Check back soon.",
    // Navigation
    home: "Home",
    brands: "Products",
    quickLinks: "Quick Links",
    searchPlaceholder: "Search products...",
    selectModel: "Select a model to see available skins",
    skinsFor: "Skins for",
    // Footer
    footerRights: "All rights reserved.",
    footerAboutDesc: "Premium Hydrogel & 3M Leather skins for cameras, phones, and laptops. Protect and personalize your devices.",
    // Newsletter
    newsletterTitle: "Stay in the Loop",
    newsletterDesc: "Get exclusive deals and new skin alerts.",
    newsletterPlaceholder: "Your email address",
    newsletterButton: "Subscribe",
    // Product Detail
    backToStore: "Back to store",
    productNotFound: "Product not found",
    variant: "Variant",
    addScreenProtector: "Add Screen Protector",
    screenProtectorDesc: "9H tempered glass, anti-fingerprint",
    addToCart: "Add to Cart",
    addToCartProtector: "Add to Cart + Protector",
    chooseModel: "Choose model",
    addedToCart: "added to cart",
    noImage: "No image",
    // Cart
    shoppingCart: "Shopping Cart",
    cartEmpty: "Your cart is empty",
    cartAddError: "Could not add this item. Please try again.",
    itemsInCart: "item(s) in your cart",
    total: "Total",
    checkoutShopify: "Checkout with Shopify",
    // Shipping banner
    freeShipping: "Free delivery in Belgium from €30",
    freeShippingEU: "Free delivery in FR, LU & NL from €50",
    shippingDelay: "Shipped within 24–48h",
    satisfiedOrRefunded: "Satisfied or refunded – 30 days",
    // FAQ
    faqTitle: "Frequently Asked Questions",
    faq1Q: "What's the difference between Hydrogel and 3M Leather?",
    faq1A: "Hydrogel is an ultra-thin (0.15mm) self-healing film ideal for a sleek, minimal look. 3M Leather is a premium textured vinyl (0.25mm) that gives your device a luxurious leather feel. Both offer excellent protection.",
    faq2Q: "How long does each skin type last?",
    faq2A: "Hydrogel skins last 2–3 years with self-healing properties. 3M Leather skins are even more durable at 3–5 years, designed to resist UV, scratches, and daily wear.",
    faq3Q: "Is application difficult?",
    faq3A: "Not at all! Hydrogel uses wet application for perfect positioning. 3M Leather features air-release technology for a bubble-free dry application. A video tutorial is included with every order.",
    faq4Q: "Do you ship internationally?",
    faq4A: "Yes, we ship worldwide. Free shipping is available on orders over €50 within Europe. International rates are calculated at checkout.",
    faq5Q: "Can I return or exchange a skin?",
    faq5A: "Absolutely. We offer a 30-day satisfaction guarantee. If you're not happy, contact us for a full refund or exchange.",
    // Auth
    signIn: "Sign In",
    createAccount: "Create Account",
    resetPassword: "Reset Password",
    signInDesc: "Welcome back to SkinVault.",
    signUpDesc: "Join SkinVault and protect your gear.",
    resetDesc: "Enter your email to receive a reset link.",
    displayName: "Display Name",
    displayNamePlaceholder: "Your name",
    emailLabel: "Email",
    passwordLabel: "Password",
    forgotPassword: "Forgot password?",
    sendResetLink: "Send Reset Link",
    noAccount: "Don't have an account?",
    haveAccount: "Already have an account?",
    checkEmail: "Check your email",
    confirmEmail: "We sent you a confirmation link. Please verify your email.",
    resetSent: "Reset link sent",
    checkEmailReset: "Check your email for the password reset link.",
    setNewPassword: "Set New Password",
    newPassword: "New Password",
    updatePassword: "Update Password",
    passwordUpdated: "Password updated successfully!",
    invalidResetLink: "This reset link is invalid or has expired.",
    myAccount: "My Account",
    signOut: "Sign Out",
    profileInfo: "Profile Information",
    saveProfile: "Save",
    profileSaved: "Profile saved!",
    memberSince: "Member since",
    orderHistory: "Order History",
    noOrders: "No orders yet",
    unfulfilled: "Unfulfilled",
    fulfilled: "Fulfilled",
    partiallyFulfilled: "Partially Fulfilled",
    // Testimonials
    testimonialsTitle: "What Our Customers Say",
    testimonialsDesc: "Join thousands of happy customers who protect their gear with SkinVault.",
    testimonial1Name: "Sophie L.",
    testimonial1Text: "The Hydrogel skin is incredible — barely visible but protects perfectly. My iPhone looks brand new!",
    testimonial2Name: "Thomas M.",
    testimonial2Text: "I went with the 3M Leather on my Canon R5. The texture is luxurious and I stopped worrying about scratches.",
    testimonial3Name: "Julie V.",
    testimonial3Text: "3M Leather on my MacBook gets compliments everywhere. The leather grain feel is just *chef's kiss*!",
    testimonial4Name: "Karim B.",
    testimonial4Text: "Ordered both types — Hydrogel for my phone, 3M Leather for the camera. Both are exceptional quality.",
    // Best Sellers
    bestSellers: "Best Sellers",
    bestSellersDesc: "Our most popular skins loved by thousands.",
    // Who Are We
    whoAreWeTitle: "Who Are We?",
    whoAreWeText: "We're a small, passionate team who believe that everyday devices deserve a touch of personality. We offer two premium materials — Hydrogel for ultra-thin protection and 3M Leather for luxurious texture — because your devices deserve the best. One skin at a time.",
    // Newsletter CTA
    newsletterBadge: "10% OFF",
    newsletterCTATitle: "Get 10% Off Your First Order",
    newsletterCTADesc: "Subscribe to our newsletter and receive an exclusive discount code for your first purchase.",
    newsletterSuccess: "You're in! Check your inbox for your code.",
    // Shipping Policy Page
    shippingPageTitle: "Shipping & Returns",
    shippingZonesTitle: "Shipping Zones",
    shippingZoneBE: "Belgium",
    shippingZoneBEDetail: "Free delivery on orders over €30. Standard shipping: €3.95.",
    shippingZoneEU: "France, Luxembourg & Netherlands",
    shippingZoneEUDetail: "Free delivery on orders over €50. Standard shipping: €5.95.",
    shippingZoneIntl: "International",
    shippingZoneIntlDetail: "Rates calculated at checkout. Usually €8.95–€14.95.",
    shippingTimesTitle: "Delivery Times",
    shippingTimeProcessing: "Processing",
    shippingTimeProcessingDetail: "Orders are processed within 24 hours on business days.",
    shippingTimeDeliveryBE: "Belgium Delivery",
    shippingTimeDeliveryBEDetail: "1–2 business days after dispatch.",
    shippingTimeDeliveryEU: "EU Delivery",
    shippingTimeDeliveryEUDetail: "2–4 business days after dispatch.",
    returnsTitle: "Returns & Refunds",
    returnsPolicy: "30-Day Guarantee",
    returnsPolicyDetail: "Not satisfied? Return your unused skin within 30 days for a full refund.",
    returnsProcess: "How to Return",
    returnsProcessDetail: "Contact us at support@skinvault.be and we'll send you a prepaid return label.",
    returnsRefund: "Refund Timeline",
    returnsRefundDetail: "Refunds are processed within 5 business days after we receive your return.",
    shippingPolicyLink: "Shipping & Returns",
    // Chat
    chatOnline: "Online — Ask me anything!",
    chatWelcome: "Hi! 👋 How can I help you today?",
    chatPlaceholder: "Type your question...",
    chatError: "Sorry, something went wrong. Please try again.",
    // Quiz
    quizBadge: "New",
    quizCTATitle: "Not Sure Which Skin Is You?",
    quizCTADesc: "Take our 1-minute style quiz and get a personalized recommendation.",
    quizCTAButton: "Take the Quiz",
    quizQ1: "What colors do you gravitate towards?",
    quizQ1A: "Bold & vibrant",
    quizQ1B: "Clean & neutral",
    quizQ1C: "Earthy & warm",
    quizQ1D: "Unique & eye-catching",
    quizQ2: "How would you describe your style?",
    quizQ2A: "Streetwear / edgy",
    quizQ2B: "Minimalist / sleek",
    quizQ2C: "Vintage / classic",
    quizQ2D: "Artsy / experimental",
    quizQ3: "Which device do you want to skin first?",
    quizQ3A: "My phone",
    quizQ3B: "My laptop",
    quizQ3C: "My camera",
    quizQ3D: "All of them!",
    quizQ4: "What vibe should your device give off?",
    quizQ4A: "Stand out from the crowd",
    quizQ4B: "Sleek & professional",
    quizQ4C: "Warm & inviting",
    quizQ4D: "A total head-turner",
    quizQ5: "Which material feels more you?",
    quizQ5A: "Hydrogel — sleek & invisible",
    quizQ5B: "3M Leather — textured & luxurious",
    quizQ5C: "Both — depends on the device",
    quizQ5D: "Not sure yet",
    quizBack: "Back",
    quizYourResult: "Your Style Match",
    quizShopNow: "Shop My Picks",
    quizRetake: "Retake Quiz",
    quizResultBoldTitle: "🔥 The Bold Statement",
    quizResultBoldDesc: "You're all about impact. 3M Leather skins with bold textures are your match — strong, edgy, and unmistakable.",
    quizResultMinimalTitle: "🤍 The Clean Minimalist",
    quizResultMinimalDesc: "Less is more. Hydrogel skins with their sleek, invisible protection fit your refined aesthetic perfectly.",
    quizResultNaturalTitle: "🌿 The Nature Lover",
    quizResultNaturalDesc: "You appreciate warmth and authenticity. 3M Leather skins bring that organic, premium feel to every device.",
    quizResultCreativeTitle: "✨ The Creative Spirit",
    quizResultCreativeDesc: "You love standing out. Try Hydrogel for printed designs or 3M Leather for textured statement pieces.",
    // Demo page
    demoLaunchingSoon: "Launching Soon",
    demoHeroTitle1: "Two materials,",
    demoHeroTitle2: "infinite style.",
    demoHeroDesc: "Hydrogel for invisible protection. 3M Leather for luxurious texture. Choose your premium material and transform any device.",
    demoDays: "Days",
    demoHours: "Hours",
    demoMins: "Mins",
    demoSecs: "Secs",
    demoEmailPlaceholder: "Your email for launch day 🎁",
    demoNotifyMe: "Notify Me",
    demoFirstOrderDiscount: "Get 15% off your first order. No spam, ever.",
    demoSneakPeek: "Sneak Peek",
    demoWhatsDropping: "What's dropping",
    demoUpcomingCollection: "Our upcoming collection in Hydrogel & 3M Leather.",
    demoSoon: "Soon",
    demoSeeTheDifference: "See the difference",
    unboxTitle: "The Experience",
    unboxHeading1: "From Package",
    unboxHeading2: "to Perfection",
    unboxStep1: "Premium Materials",
    unboxStep2: "Peel the Backing",
    unboxStep3: "Apply with Precision",
    unboxStep4: "Perfection Achieved",
    demoDragSlider: "Drag the slider to reveal the transformation",
    demoWhySkinVault: "Why SkinVault?",
    demoEngineered: "Two premium materials, engineered for perfection",
    demoFeature1Title: "Hydrogel Protection",
    demoFeature1Desc: "Self-healing, ultra-thin film that wraps seamlessly around every edge.",
    demoFeature2Title: "3M Leather Luxury",
    demoFeature2Desc: "Genuine 3M textured vinyl with rich leather grain for a premium feel.",
    demoFeature3Title: "Bubble-Free Application",
    demoFeature3Desc: "Both materials feature easy application for a flawless, professional finish.",
    demoFeature4Title: "Premium Quality",
    demoFeature4Desc: "Only the finest materials — Hydrogel film and genuine 3M vinyl.",
    demoStat1: "Skins applied",
    demoStat2: "Premium materials",
    demoStat3: "Average rating",
    demoStat4: "Ultra-thin",
    demoOurStory: "Our story",
    demoStory1: "We started SkinVault because we were tired of boring, bulky cases. Your tech should look as good as it performs.",
    demoStory2: "We offer two premium materials: Hydrogel for ultra-thin, self-healing protection, and 3M Leather for a luxurious textured feel. Each precision-cut for a flawless fit.",
    demoStory3: "From sleek Hydrogel to rich 3M Leather — we create skins for people who want their devices to stand out.",
    demoDontMissLaunch: "Don't miss the launch",
    demoJoinWaitlist: "Join the waitlist and get 15% off your first order.",
    demoJoin: "Join",
    demoInvalidEmail: "Please enter a valid email.",
    demoSignupSuccess: "You're on the list! 🎉 We'll notify you at launch.",
    // Pre-launch
    prelaunchCollectionTag: "Collection Preview",
    prelaunchCollectionTitle1: "Explore the",
    prelaunchCollectionTitle2: "Collection",
    prelaunchCollectionDesc: "A curated selection of premium skins, crafted for every device you own.",
    prelaunchGridTitle1: "Every design,",
    prelaunchGridTitle2: "one vision.",
    prelaunchGridDesc: "From marble textures to genuine leather. See what's waiting for you.",
    prelaunchCTATitle1: "Be the first to",
    prelaunchCTATitle2: "get yours.",
    prelaunchCTADesc: "Sign up now and we'll let you know the moment we go live.",
    prelaunchAllRights: "All rights reserved.",
    // Configurator
    cfgBadge: "Skin Builder",
    cfgTitle: "Design Your Perfect Skin",
    cfgDesc: "Choose your device, material type, design, and preview it in real-time.",
    cfgDevice: "Choose Device",
    cfgMaterial: "Material Type",
    cfgSkin: "Choose Design",
    cfgFinish: "Choose Finish",
    cfgOptions: "Options",
    cfgLogoCutout: "Logo Cutout",
    cfgLogoCutoutDesc: "Precision-cut opening for your device logo (Complimentary)",
    cfgSideSkins: "Side Skins",
    cfgSideSkinsDesc: "Add protective skin to device sides (+€2.00)",
    cfgFront: "Front",
    cfgBack: "Back",
    cfgCompare: "Compare",
    cfgFrontView: "Front View",
    cfgBackView: "Back View",
    cfgHoverZoom: "Hover to zoom in on texture",
    cfgSelectSkin: "Select a skin to preview",
    cfgSelectToSee: "Select a device and skin to see pricing",
    cfgIncluded: "Included",
    cfgComingSoon: "Coming Soon",
    cfgFreeShipping: "Free shipping on orders over €30",
    cfgHydrogel: "Hydrogel",
    cfgLeatherLabel: "3M Leather",
    cfgHydrogelSpecs: "0.15mm · Self-healing",
    cfgLeatherSpecs: "0.25mm · Textured grip",
    cfgFrom: "from",
    cfgCTA: "Build Your Skin",
    cfgAddToCart: "Add to Cart",
    cfgAdding: "Adding...",
    cfgAdded: "Added!",
    cfgAddedToCart: "Added to cart!",
    cfgWithProduct: "with product",
    // Gallery section
    galleryTitle1: "Designed to",
    galleryTitle2: "stand out.",
    galleryDesc: "Premium Hydrogel & 3M Leather skins crafted for perfection.",
    // Skin Builder CTA section
    skinBuilderLabel: "Skin Builder",
    skinBuilderNew: "NEW",
    skinBuilderCTATitle1: "Design Your",
    skinBuilderCTATitle2: "Perfect Skin",
    skinBuilderCTADesc: "Choose between Hydrogel or 3M Leather, pick your design, and preview in our interactive builder.",
    skinBuilderOpen: "Open Skin Builder",
    // Device selector
    fromPrice: "From",
    // Tutorial page
    tutorialBadge: "Application Guides",
    tutorialTitle1: "How to Apply Your",
    tutorialTitle2: "Skin",
    tutorialIntro: "Step-by-step video tutorials for every product we offer — phones, tablets, laptops, consoles and accessories. Pick your device below to get started.",
    tutorialTabPhone: "Phone",
    tutorialTabTablet: "Tablet",
    tutorialTabLaptop: "Laptop / PC",
    tutorialTabConsole: "Console",
    tutorialTabAccessories: "Accessories",
    tutorialDifficultyEasy: "Easy",
    tutorialDifficultyMedium: "Medium",
    tutorialDifficultyAdvanced: "Advanced",
    tutorialWatchInstall: "Watch installation →",
    tutorialWatchAria: "Watch installation on YouTube",
    tutorialNeedHelpTitle: "Need more help?",
    tutorialNeedHelpDesc: "Can't find a tutorial for your device? Our support team is happy to walk you through the application process.",
    tutorialContactSupport: "Contact Support",
    tutorialCatBackProtectionLabel: "Back Protection",
    tutorialCatBackProtectionDesc: "Hydrogel back protector — shields your device from scratches and scuffs.",
    tutorialCatBackSkinLabel: "Back Skin",
    tutorialCatBackSkinDesc: "Premium 3M leather and vinyl back skins for a luxury finish.",
    tutorialCatScreenProtectionLabel: "Screen Protection",
    tutorialCatScreenProtectionDesc: "Self-healing hydrogel screen protector — full edge-to-edge coverage.",
    tutorialCatAllAroundLabel: "All-Around Phone Skin",
    tutorialCatAllAroundDesc: "Full-coverage skin: back, sides, edges and camera ring.",
    tutorialCatTabletBackLabel: "Tablet Back Skin",
    tutorialCatTabletBackDesc: "Cover the back of your iPad or Galaxy Tab with premium materials.",
    tutorialCatTabletScreenLabel: "Tablet Screen Protection",
    tutorialCatTabletScreenDesc: "Paper-feel and hydrogel screen protectors for tablets.",
    tutorialCatLaptopLabel: "Laptop Skin",
    tutorialCatLaptopDesc: "MacBook and laptop skins — lid, palm rest, and bottom panel.",
    tutorialCatConsoleLabel: "Console & Controller Skins",
    tutorialCatConsoleDesc: "PlayStation, Xbox and Nintendo Switch — full kits and controller wraps.",
    tutorialCatAccessoriesLabel: "Headphones & Accessories",
    tutorialCatAccessoriesDesc: "AirPods cases, headphone cups and other small accessories.",
    trackOrderLink: "Track your order",
    trackBadge: "Order Tracking",
    trackTitle: "Track your order",
    trackSubtitle: "Enter your tracking number to see the latest delivery updates.",
    trackPlaceholder: "e.g. LX123456789FR",
    trackButton: "Track",
    trackNumberLabel: "Tracking number",
    trackCourier: "Carrier",
    trackHistory: "Tracking history",
    trackNoEvents: "No tracking events yet. Please check again later.",
    trackError: "Unable to fetch tracking information.",
    trackStatusDelivered: "Delivered",
    trackStatusInTransit: "In transit",
    trackStatusPending: "Pending",
    trackStatusException: "Exception",
    trackStatusUnknown: "Unknown",
  },
  fr: {
    heroTag: "Skins Premium pour Appareils",
    heroPhoneTitle1: "Protégez votre",
    heroPhoneTitle2: "téléphone avec style.",
    heroPhoneDesc: "Choisissez entre Hydrogel ou Cuir 3M pour iPhone & Samsung. Deux matériaux premium, un ajustement parfait.",
    heroCameraTitle1: "Sublimez votre",
    heroCameraTitle2: "appareil photo.",
    heroCameraDesc: "Skins Hydrogel ou Cuir 3M pour Canon & Nikon. Protégez votre boîtier avec un look qui se démarque.",
    heroMacbookTitle1: "Transformez votre",
    heroMacbookTitle2: "MacBook.",
    heroMacbookDesc: "Skins Hydrogel ou Cuir 3M pour MacBook Pro & Air. Faites de votre laptop une pièce unique.",
    heroControllerTitle1: "Personnalisez votre",
    heroControllerTitle2: "manette PS5.",
    heroControllerDesc: "Skins Hydrogel pour manettes PS5 DualSense. 6 designs exclusifs pour votre setup gaming.",
    heroTelephoneTitle1: "Style pour votre",
    heroTelephoneTitle2: "téléphone.",
    heroTelephoneDesc: "Skins Hydrogel et Cuir 3M pour les derniers iPhone, Samsung et plus.",
    heroComputerTitle1: "Rehaussez votre",
    heroComputerTitle2: "ordinateur.",
    heroComputerDesc: "Skins Vinyle 3M pour MacBook, Dell, Lenovo et autres laptops populaires.",
    heroConsoleTitle1: "Personnalisez votre",
    heroConsoleTitle2: "console.",
    heroConsoleDesc: "Skins Vinyle 3M pour PS5, PS4, Xbox et Nintendo Switch.",
    heroCameraPhotoTitle1: "Protégez votre",
    heroCameraPhotoTitle2: "appareil photo.",
    heroCameraPhotoDesc: "Skins Vinyle 3M pour appareils photo et objectifs Canon, Nikon et Sony.",
    telephones: "Téléphones",
    computers: "Ordinateurs",
    consoles: "Consoles",
    cameraPhoto: "Appareil photo",
    shopNow: "Acheter",
    dragToCompare: "← Glisser pour comparer →",
    before: "Avant",
    withSkin: "Avec Skin",
    twoTypesTag: "Deux Matériaux Premium",
    twoTypesTitle1: "Choisissez votre",
    twoTypesTitle2: "Matériau",
    twoTypesDesc: "Chaque appareil mérite le skin parfait. Choisissez entre nos deux matériaux premium, chacun conçu pour une expérience différente.",
    hydrogel: "Hydrogel",
    hydrogelTag: "Protection Ultra-Fine",
    hydrogelDesc: "Un film auto-cicatrisant et ultra-flexible qui épouse les bords parfaitement. Designs cristallins ou imprimés avec revêtement anti-traces.",
    hydrogelFeature1: "Technologie auto-cicatrisante",
    hydrogelFeature2: "0,15mm ultra-fin",
    hydrogelFeature3: "Revêtement anti-traces",
    hydrogelFeature4: "Enveloppement flexible des bords",
    hydrogelPrice: "Dès 14,99 €",
    leather3m: "Cuir 3M",
    leather3mTag: "Texture Premium",
    leather3mDesc: "Vinyle texturé 3M authentique avec un grain cuir riche. Un toucher luxueux qui transforme votre appareil en pièce d'exception.",
    leather3mFeature1: "Vinyle 3M authentique",
    leather3mFeature2: "Texture cuir riche",
    leather3mFeature3: "Résistant UV & rayures",
    leather3mFeature4: "Découpe laser de précision",
    leather3mPrice: "Dès 19,99 €",
    comparisonTitle: "Côte à côte",
    compThickness: "Épaisseur",
    compThicknessHydro: "0,15mm",
    compThicknessLeather: "0,25mm",
    compTexture: "Texture",
    compTextureHydro: "Lisse / Brillant",
    compTextureLeather: "Grain cuir riche",
    compProtection: "Protection",
    compProtectionHydro: "Film auto-cicatrisant",
    compProtectionLeather: "Bouclier anti-rayures 3M",
    compDurability: "Durabilité",
    compDurabilityHydro: "2–3 ans",
    compDurabilityLeather: "3–5 ans",
    compApplication: "Application",
    compApplicationHydro: "Application humide, sans bulles",
    compApplicationLeather: "Application sèche, libération d'air",
    compBestFor: "Idéal pour",
    compBestForHydro: "Look épuré, minimal",
    compBestForLeather: "Luxe, sensation tactile",
    exploreSkins: "Explorer les skins",
    feature1Title: "Deux matériaux premium",
    feature1Desc: "Choisissez Hydrogel pour la finesse ou Cuir 3M pour la texture luxueuse.",
    feature2Title: "Zéro épaisseur",
    feature2Desc: "Skins ultra-fins qui n'ajoutent aucune épaisseur à votre appareil.",
    feature3Title: "Application sans bulles",
    feature3Desc: "Technologie de libération d'air pour un résultat impeccable à chaque fois.",
    howItWorks: "Comment ça marche",
    howItWorksDesc: "Trois étapes simples pour protéger votre équipement.",
    step1Title: "Choisissez votre appareil",
    step1Desc: "Sélectionnez la marque et le modèle dans notre catalogue.",
    step2Title: "Choisissez votre matériau",
    step2Desc: "Choisissez entre Hydrogel ou Cuir 3M, puis votre design.",
    step3Title: "Appliquez & Profitez",
    step3Desc: "Application sans bulles en quelques minutes seulement.",
    cameras: "Appareils photo",
    iphones: "Téléphones",
    macbooks: "MacBooks",
    controllers: "Manettes",
    accessories: "Accessoires",
    cardSkins: "Skins pour cartes",
    laptops: "Ordinateurs portables",
    tablets: "Tablettes",
    all: "Tout",
    allProducts: "Tous les produits",
    allModels: "Tous les modèles",
    shop: "Boutique",
    discover: "Découvrir",
    help: "Aide & Compte",
    quizTitle: "Trouveur de style",
    tutorials: "Guide d'application",
    returns: "Retours & Remboursements",
    skinsSuffix: "Skins",
    comingSoon: "Bientôt disponible",
    comingSoonDesc: "Cette collection arrive bientôt. Repassez vite — nous préparons quelque chose de spécial.",
    categoryHeroDesc: "Skins Hydrogel et Cuir 3M, découpés sur mesure pour chaque modèle.",
    noProducts: "Aucun produit trouvé",
    noProductsHint: "De nouveaux produits sont en préparation pour cette catégorie. Revenez bientôt.",
    home: "Accueil",
    brands: "Produits",
    quickLinks: "Liens rapides",
    searchPlaceholder: "Rechercher...",
    selectModel: "Sélectionnez un modèle pour voir les skins disponibles",
    skinsFor: "Skins pour",
    footerRights: "Tous droits réservés.",
    footerAboutDesc: "Skins Hydrogel & Cuir 3M premium pour appareils photo, téléphones et ordinateurs. Protégez et personnalisez vos appareils.",
    newsletterTitle: "Restez informé",
    newsletterDesc: "Recevez des offres exclusives et les nouveautés.",
    newsletterPlaceholder: "Votre adresse email",
    newsletterButton: "S'abonner",
    backToStore: "Retour à la boutique",
    productNotFound: "Produit introuvable",
    variant: "Variante",
    addScreenProtector: "Ajouter un protecteur d'écran",
    screenProtectorDesc: "Verre trempé 9H, anti-traces",
    addToCart: "Ajouter au panier",
    addToCartProtector: "Ajouter au panier + Protecteur",
    chooseModel: "Choisir le modèle",
    addedToCart: "ajouté au panier",
    noImage: "Pas d'image",
    shoppingCart: "Panier",
    cartEmpty: "Votre panier est vide",
    cartAddError: "Impossible d’ajouter cet article. Veuillez réessayer.",
    itemsInCart: "article(s) dans votre panier",
    total: "Total",
    checkoutShopify: "Payer avec Shopify",
    freeShipping: "Livraison gratuite en Belgique dès 30 €",
    freeShippingEU: "Livraison gratuite en FR, LU & NL dès 50 €",
    shippingDelay: "Expédié sous 24–48h",
    satisfiedOrRefunded: "Satisfait ou remboursé – 30 jours",
    faqTitle: "Questions fréquentes",
    faq1Q: "Quelle est la différence entre Hydrogel et Cuir 3M ?",
    faq1A: "L'Hydrogel est un film ultra-fin (0,15mm) auto-cicatrisant idéal pour un look épuré. Le Cuir 3M est un vinyle texturé premium (0,25mm) qui donne à votre appareil une sensation de cuir luxueux. Les deux offrent une excellente protection.",
    faq2Q: "Combien de temps dure chaque type de skin ?",
    faq2A: "Les skins Hydrogel durent 2 à 3 ans avec des propriétés auto-cicatrisantes. Les skins Cuir 3M sont encore plus durables (3 à 5 ans), conçus pour résister aux UV, rayures et usure quotidienne.",
    faq3Q: "L'application est-elle difficile ?",
    faq3A: "Pas du tout ! L'Hydrogel utilise une application humide pour un positionnement parfait. Le Cuir 3M dispose d'une technologie de libération d'air pour une application sèche sans bulles. Un tutoriel vidéo est inclus avec chaque commande.",
    faq4Q: "Livrez-vous à l'international ?",
    faq4A: "Oui, nous livrons dans le monde entier. La livraison gratuite est disponible pour les commandes de plus de 50 € en Europe.",
    faq5Q: "Puis-je retourner ou échanger un skin ?",
    faq5A: "Absolument. Nous offrons une garantie satisfaction de 30 jours. Si vous n'êtes pas satisfait, contactez-nous pour un remboursement ou un échange.",
    signIn: "Se connecter",
    createAccount: "Créer un compte",
    resetPassword: "Réinitialiser le mot de passe",
    signInDesc: "Bienvenue sur SkinVault.",
    signUpDesc: "Rejoignez SkinVault et protégez votre équipement.",
    resetDesc: "Entrez votre email pour recevoir un lien de réinitialisation.",
    displayName: "Nom d'affichage",
    displayNamePlaceholder: "Votre nom",
    emailLabel: "Email",
    passwordLabel: "Mot de passe",
    forgotPassword: "Mot de passe oublié ?",
    sendResetLink: "Envoyer le lien",
    noAccount: "Pas encore de compte ?",
    haveAccount: "Déjà un compte ?",
    checkEmail: "Vérifiez votre email",
    confirmEmail: "Nous vous avons envoyé un lien de confirmation. Veuillez vérifier votre email.",
    resetSent: "Lien envoyé",
    checkEmailReset: "Vérifiez votre email pour le lien de réinitialisation.",
    setNewPassword: "Nouveau mot de passe",
    newPassword: "Nouveau mot de passe",
    updatePassword: "Mettre à jour",
    passwordUpdated: "Mot de passe mis à jour !",
    invalidResetLink: "Ce lien est invalide ou a expiré.",
    myAccount: "Mon compte",
    signOut: "Se déconnecter",
    profileInfo: "Informations du profil",
    saveProfile: "Enregistrer",
    profileSaved: "Profil enregistré !",
    memberSince: "Membre depuis",
    orderHistory: "Historique des commandes",
    noOrders: "Aucune commande pour le moment",
    unfulfilled: "Non expédié",
    fulfilled: "Expédié",
    partiallyFulfilled: "Partiellement expédié",
    testimonialsTitle: "Ce que disent nos clients",
    testimonialsDesc: "Rejoignez des milliers de clients satisfaits qui protègent leur matériel avec SkinVault.",
    testimonial1Name: "Sophie L.",
    testimonial1Text: "Le skin Hydrogel est incroyable — à peine visible mais protège parfaitement. Mon iPhone a l'air tout neuf !",
    testimonial2Name: "Thomas M.",
    testimonial2Text: "J'ai choisi le Cuir 3M pour mon Canon R5. La texture est luxueuse et je ne m'inquiète plus des rayures.",
    testimonial3Name: "Julie V.",
    testimonial3Text: "Le Cuir 3M sur mon MacBook me vaut des compliments partout. Le grain cuir, c'est juste *parfait* !",
    testimonial4Name: "Karim B.",
    testimonial4Text: "J'ai commandé les deux — Hydrogel pour mon téléphone, Cuir 3M pour l'appareil photo. Les deux sont exceptionnels.",
    bestSellers: "Meilleures ventes",
    bestSellersDesc: "Nos skins les plus populaires adorés par des milliers.",
    whoAreWeTitle: "Qui sommes-nous ?",
    whoAreWeText: "Nous sommes une petite équipe passionnée. Nous proposons deux matériaux premium — Hydrogel pour une protection ultra-fine et Cuir 3M pour une texture luxueuse — parce que vos appareils méritent le meilleur. Un skin à la fois.",
    newsletterBadge: "-10%",
    newsletterCTATitle: "10% de réduction sur votre 1ère commande",
    newsletterCTADesc: "Inscrivez-vous à notre newsletter et recevez un code de réduction exclusif.",
    newsletterSuccess: "C'est fait ! Vérifiez votre boîte mail pour votre code.",
    shippingPageTitle: "Livraison & Retours",
    shippingZonesTitle: "Zones de livraison",
    shippingZoneBE: "Belgique",
    shippingZoneBEDetail: "Livraison gratuite dès 30 €. Livraison standard : 3,95 €.",
    shippingZoneEU: "France, Luxembourg & Pays-Bas",
    shippingZoneEUDetail: "Livraison gratuite dès 50 €. Livraison standard : 5,95 €.",
    shippingZoneIntl: "International",
    shippingZoneIntlDetail: "Tarifs calculés au moment du paiement. Généralement 8,95 € – 14,95 €.",
    shippingTimesTitle: "Délais de livraison",
    shippingTimeProcessing: "Traitement",
    shippingTimeProcessingDetail: "Les commandes sont traitées sous 24h les jours ouvrés.",
    shippingTimeDeliveryBE: "Livraison Belgique",
    shippingTimeDeliveryBEDetail: "1–2 jours ouvrés après expédition.",
    shippingTimeDeliveryEU: "Livraison UE",
    shippingTimeDeliveryEUDetail: "2–4 jours ouvrés après expédition.",
    returnsTitle: "Retours & Remboursements",
    returnsPolicy: "Garantie 30 jours",
    returnsPolicyDetail: "Pas satisfait ? Retournez votre skin non utilisé sous 30 jours pour un remboursement complet.",
    returnsProcess: "Comment retourner",
    returnsProcessDetail: "Contactez-nous à support@skinvault.be et nous vous enverrons une étiquette retour prépayée.",
    returnsRefund: "Délai de remboursement",
    returnsRefundDetail: "Les remboursements sont traités sous 5 jours ouvrés après réception du retour.",
    shippingPolicyLink: "Livraison & Retours",
    chatOnline: "En ligne — Posez-moi vos questions !",
    chatWelcome: "Bonjour ! 👋 Comment puis-je vous aider ?",
    chatPlaceholder: "Tapez votre question...",
    chatError: "Désolé, une erreur s'est produite. Réessayez.",
    quizBadge: "Nouveau",
    quizCTATitle: "Pas sûr(e) de votre skin ?",
    quizCTADesc: "Faites notre quiz style d'1 minute et recevez une recommandation personnalisée.",
    quizCTAButton: "Faire le Quiz",
    quizQ1: "Quelles couleurs vous attirent ?",
    quizQ1A: "Vives & audacieuses",
    quizQ1B: "Neutres & épurées",
    quizQ1C: "Chaudes & terreuses",
    quizQ1D: "Uniques & accrocheuses",
    quizQ2: "Comment décririez-vous votre style ?",
    quizQ2A: "Streetwear / rebelle",
    quizQ2B: "Minimaliste / élégant",
    quizQ2C: "Vintage / classique",
    quizQ2D: "Artistique / expérimental",
    quizQ3: "Quel appareil voulez-vous personnaliser en premier ?",
    quizQ3A: "Mon téléphone",
    quizQ3B: "Mon laptop",
    quizQ3C: "Mon appareil photo",
    quizQ3D: "Tous !",
    quizQ4: "Quelle ambiance pour votre appareil ?",
    quizQ4A: "Se démarquer de la masse",
    quizQ4B: "Élégant & professionnel",
    quizQ4C: "Chaleureux & accueillant",
    quizQ4D: "Époustouflant",
    quizQ5: "Quel matériau vous correspond le mieux ?",
    quizQ5A: "Hydrogel — épuré & invisible",
    quizQ5B: "Cuir 3M — texturé & luxueux",
    quizQ5C: "Les deux — selon l'appareil",
    quizQ5D: "Pas encore sûr(e)",
    quizBack: "Retour",
    quizYourResult: "Votre Style",
    quizShopNow: "Voir mes skins",
    quizRetake: "Refaire le quiz",
    quizResultBoldTitle: "🔥 L'Audacieux",
    quizResultBoldDesc: "Vous aimez l'impact. Les skins Cuir 3M avec des textures audacieuses sont faits pour vous.",
    quizResultMinimalTitle: "🤍 Le Minimaliste",
    quizResultMinimalDesc: "Moins c'est plus. Les skins Hydrogel avec leur protection épurée et invisible correspondent parfaitement à votre esthétique.",
    quizResultNaturalTitle: "🌿 L'Amoureux de la Nature",
    quizResultNaturalDesc: "Vous appréciez l'authenticité. Les skins Cuir 3M apportent cette sensation premium et organique à tous vos appareils.",
    quizResultCreativeTitle: "✨ L'Esprit Créatif",
    quizResultCreativeDesc: "Vous aimez briller. Essayez l'Hydrogel pour les designs imprimés ou le Cuir 3M pour les pièces texturées.",
    demoLaunchingSoon: "Lancement imminent",
    demoHeroTitle1: "Deux matériaux,",
    demoHeroTitle2: "style infini.",
    demoHeroDesc: "Hydrogel pour une protection invisible. Cuir 3M pour une texture luxueuse. Choisissez votre matériau premium et transformez votre appareil.",
    demoDays: "Jours",
    demoHours: "Heures",
    demoMins: "Min",
    demoSecs: "Sec",
    demoEmailPlaceholder: "Votre email pour le jour J 🎁",
    demoNotifyMe: "Me notifier",
    demoFirstOrderDiscount: "15% de réduction sur votre 1ère commande. Pas de spam.",
    demoSneakPeek: "Avant-première",
    demoWhatsDropping: "Bientôt disponible",
    demoUpcomingCollection: "Notre prochaine collection en Hydrogel & Cuir 3M.",
    demoSoon: "Bientôt",
    demoSeeTheDifference: "Voyez la différence",
    unboxTitle: "L'expérience",
    unboxHeading1: "De l'emballage",
    unboxHeading2: "à la perfection",
    unboxStep1: "Matériaux Premium",
    unboxStep2: "Décollez le support",
    unboxStep3: "Appliquez avec précision",
    unboxStep4: "Perfection atteinte",
    demoDragSlider: "Glissez le curseur pour révéler la transformation",
    demoWhySkinVault: "Pourquoi SkinVault ?",
    demoEngineered: "Deux matériaux premium, conçus pour la perfection",
    demoFeature1Title: "Protection Hydrogel",
    demoFeature1Desc: "Film auto-cicatrisant ultra-fin qui épouse parfaitement chaque bord.",
    demoFeature2Title: "Luxe Cuir 3M",
    demoFeature2Desc: "Vinyle 3M texturé authentique avec grain cuir riche pour un toucher premium.",
    demoFeature3Title: "Application sans bulles",
    demoFeature3Desc: "Les deux matériaux offrent une application facile pour un résultat impeccable.",
    demoFeature4Title: "Qualité Premium",
    demoFeature4Desc: "Uniquement les meilleurs matériaux — film Hydrogel et vinyle 3M authentique.",
    demoStat1: "Skins appliqués",
    demoStat2: "Matériaux premium",
    demoStat3: "Note moyenne",
    demoStat4: "Ultra-fin",
    demoOurStory: "Notre histoire",
    demoStory1: "Nous avons créé SkinVault parce que nous en avions marre des coques ennuyeuses. Votre tech devrait être aussi belle que performante.",
    demoStory2: "Nous proposons deux matériaux premium : Hydrogel pour une protection ultra-fine et auto-cicatrisante, et Cuir 3M pour une sensation texturée luxueuse.",
    demoStory3: "De l'Hydrogel épuré au Cuir 3M riche — nous créons des skins pour ceux qui veulent se démarquer.",
    demoDontMissLaunch: "Ne manquez pas le lancement",
    demoJoinWaitlist: "Rejoignez la liste d'attente et obtenez 15% de réduction.",
    demoJoin: "Rejoindre",
    demoInvalidEmail: "Veuillez entrer un email valide.",
    demoSignupSuccess: "Vous êtes inscrit ! 🎉 Nous vous notifierons au lancement.",
    prelaunchCollectionTag: "Aperçu de la collection",
    prelaunchCollectionTitle1: "Explorez la",
    prelaunchCollectionTitle2: "collection",
    prelaunchCollectionDesc: "Une sélection de skins premium, conçus pour chacun de vos appareils.",
    prelaunchGridTitle1: "Chaque design,",
    prelaunchGridTitle2: "une vision.",
    prelaunchGridDesc: "Des textures marbre au cuir véritable. Découvrez ce qui vous attend.",
    prelaunchCTATitle1: "Soyez les premiers à",
    prelaunchCTATitle2: "obtenir le vôtre.",
    prelaunchCTADesc: "Inscrivez-vous et nous vous préviendrons dès l'ouverture.",
    prelaunchAllRights: "Tous droits réservés.",
    cfgBadge: "Créateur de Skin",
    cfgTitle: "Créez votre skin parfait",
    cfgDesc: "Choisissez votre appareil, type de matériau, design, et prévisualisez en temps réel.",
    cfgDevice: "Choisir l'appareil",
    cfgMaterial: "Type de matériau",
    cfgSkin: "Choisir le design",
    cfgFinish: "Choisir la finition",
    cfgOptions: "Options",
    cfgLogoCutout: "Découpe logo",
    cfgLogoCutoutDesc: "Ouverture précise pour le logo de votre appareil (Offert)",
    cfgSideSkins: "Skins latéraux",
    cfgSideSkinsDesc: "Ajoutez un skin protecteur sur les côtés (+2,00 €)",
    cfgFront: "Avant",
    cfgBack: "Arrière",
    cfgCompare: "Comparer",
    cfgFrontView: "Vue avant",
    cfgBackView: "Vue arrière",
    cfgHoverZoom: "Survolez pour zoomer sur la texture",
    cfgSelectSkin: "Sélectionnez un skin pour prévisualiser",
    cfgSelectToSee: "Sélectionnez un appareil et un skin pour voir le prix",
    cfgIncluded: "Inclus",
    cfgComingSoon: "Bientôt disponible",
    cfgFreeShipping: "Livraison gratuite dès 30 €",
    cfgHydrogel: "Hydrogel",
    cfgLeatherLabel: "Cuir 3M",
    cfgHydrogelSpecs: "0,15mm · Auto-cicatrisant",
    cfgLeatherSpecs: "0,25mm · Grip texturé",
    cfgFrom: "à partir de",
    cfgCTA: "Créez votre skin",
    cfgAddToCart: "Ajouter au panier",
    cfgAdding: "Ajout...",
    cfgAdded: "Ajouté !",
    cfgAddedToCart: "Ajouté au panier !",
    cfgWithProduct: "avec le produit",
    galleryTitle1: "Conçu pour",
    galleryTitle2: "se démarquer.",
    galleryDesc: "Skins Hydrogel & Cuir 3M premium, conçus pour la perfection.",
    skinBuilderLabel: "Créateur de Skin",
    skinBuilderNew: "NOUVEAU",
    skinBuilderCTATitle1: "Créez votre",
    skinBuilderCTATitle2: "Skin Parfait",
    skinBuilderCTADesc: "Choisissez entre Hydrogel ou Cuir 3M, votre design, et prévisualisez dans notre créateur interactif.",
    skinBuilderOpen: "Ouvrir le créateur",
    fromPrice: "Dès",
    // Tutorial page
    tutorialBadge: "Guides d'application",
    tutorialTitle1: "Comment poser votre",
    tutorialTitle2: "skin",
    tutorialIntro: "Tutoriels vidéo pas à pas pour chaque produit que nous proposons — téléphones, tablettes, ordinateurs portables, consoles et accessoires. Choisissez votre appareil ci-dessous pour commencer.",
    tutorialTabPhone: "Téléphone",
    tutorialTabTablet: "Tablette",
    tutorialTabLaptop: "Ordinateur / PC",
    tutorialTabConsole: "Console",
    tutorialTabAccessories: "Accessoires",
    tutorialDifficultyEasy: "Facile",
    tutorialDifficultyMedium: "Moyen",
    tutorialDifficultyAdvanced: "Avancé",
    tutorialWatchInstall: "Voir l'installation →",
    tutorialWatchAria: "Voir l'installation sur YouTube",
    tutorialNeedHelpTitle: "Besoin d'aide ?",
    tutorialNeedHelpDesc: "Vous ne trouvez pas de tutoriel pour votre appareil ? Notre équipe support se fera un plaisir de vous accompagner dans la pose.",
    tutorialContactSupport: "Contacter le support",
    tutorialCatBackProtectionLabel: "Protection arrière",
    tutorialCatBackProtectionDesc: "Protecteur arrière en hydrogel — protège votre appareil des rayures et frottements.",
    tutorialCatBackSkinLabel: "Skin arrière",
    tutorialCatBackSkinDesc: "Skins arrière premium en cuir 3M et vinyle pour une finition luxueuse.",
    tutorialCatScreenProtectionLabel: "Protection d'écran",
    tutorialCatScreenProtectionDesc: "Protecteur d'écran hydrogel auto-cicatrisant — couverture intégrale bord à bord.",
    tutorialCatAllAroundLabel: "Skin intégral téléphone",
    tutorialCatAllAroundDesc: "Skin couvrant tout : dos, côtés, bords et contour de l'objectif.",
    tutorialCatTabletBackLabel: "Skin arrière tablette",
    tutorialCatTabletBackDesc: "Habillez l'arrière de votre iPad ou Galaxy Tab avec des matériaux premium.",
    tutorialCatTabletScreenLabel: "Protection d'écran tablette",
    tutorialCatTabletScreenDesc: "Protecteurs d'écran toucher papier et hydrogel pour tablettes.",
    tutorialCatLaptopLabel: "Skin ordinateur",
    tutorialCatLaptopDesc: "Skins MacBook et ordinateurs portables — capot, repose-poignet et panneau inférieur.",
    tutorialCatConsoleLabel: "Skins consoles & manettes",
    tutorialCatConsoleDesc: "PlayStation, Xbox et Nintendo Switch — kits complets et habillages de manettes.",
    tutorialCatAccessoriesLabel: "Casques & accessoires",
    tutorialCatAccessoriesDesc: "Boîtiers AirPods, coussinets de casques et autres petits accessoires.",
    trackOrderLink: "Suivre ma commande",
    trackBadge: "Suivi de commande",
    trackTitle: "Suivez votre commande",
    trackSubtitle: "Saisissez votre numéro de suivi pour voir les dernières mises à jour de livraison.",
    trackPlaceholder: "ex : LX123456789FR",
    trackButton: "Suivre",
    trackNumberLabel: "Numéro de suivi",
    trackCourier: "Transporteur",
    trackHistory: "Historique du suivi",
    trackNoEvents: "Aucun événement de suivi pour le moment. Veuillez revenir plus tard.",
    trackError: "Impossible de récupérer les informations de suivi.",
    trackStatusDelivered: "Livré",
    trackStatusInTransit: "En transit",
    trackStatusPending: "En attente",
    trackStatusException: "Anomalie",
    trackStatusUnknown: "Inconnu",
  },
  nl: {
    heroTag: "Premium Apparaat Skins",
    heroPhoneTitle1: "Bescherm je",
    heroPhoneTitle2: "telefoon met stijl.",
    heroPhoneDesc: "Kies tussen Hydrogel of 3M Leer skins voor iPhones & Samsung. Twee premium materialen, één perfecte pasvorm.",
    heroCameraTitle1: "Verbeter je",
    heroCameraTitle2: "camera setup.",
    heroCameraDesc: "Hydrogel of 3M Leer skins voor Canon & Nikon. Bescherm je camerabody met een look die opvalt.",
    heroMacbookTitle1: "Verander je",
    heroMacbookTitle2: "MacBook look.",
    heroMacbookDesc: "Hydrogel of 3M Leer skins voor MacBook Pro & Air. Maak van je laptop een uniek statement.",
    heroControllerTitle1: "Upgrade je",
    heroControllerTitle2: "controller stijl.",
    heroControllerDesc: "Hydrogel skins voor PS5 DualSense controllers. 6 exclusieve designs voor je gaming setup.",
    heroTelephoneTitle1: "Stijl voor je",
    heroTelephoneTitle2: "telefoon.",
    heroTelephoneDesc: "Hydrogel en 3M Leer skins voor de nieuwste iPhone, Samsung en meer.",
    heroComputerTitle1: "Upgrade je",
    heroComputerTitle2: "computer.",
    heroComputerDesc: "3M Vinyl skins voor MacBook, Dell, Lenovo en andere populaire laptops.",
    heroConsoleTitle1: "Personaliseer je",
    heroConsoleTitle2: "console.",
    heroConsoleDesc: "3M Vinyl skins voor PS5, PS4, Xbox en Nintendo Switch.",
    heroCameraPhotoTitle1: "Bescherm je",
    heroCameraPhotoTitle2: "camera.",
    heroCameraPhotoDesc: "3M Vinyl skins voor Canon, Nikon en Sony camera's & lenzen.",
    telephones: "Telefoons",
    computers: "Computers",
    consoles: "Consoles",
    cameraPhoto: "Camera",
    shopNow: "Kopen",
    dragToCompare: "← Sleep om te vergelijken →",
    before: "Voor",
    withSkin: "Met Skin",
    twoTypesTag: "Twee Premium Materialen",
    twoTypesTitle1: "Kies je",
    twoTypesTitle2: "Materiaal",
    twoTypesDesc: "Elk apparaat verdient de perfecte skin. Kies tussen onze twee premium materialen, elk ontworpen voor een andere ervaring.",
    hydrogel: "Hydrogel",
    hydrogelTag: "Ultradunne Bescherming",
    hydrogelDesc: "Een zelfherstellende, ultra-flexibele film die naadloos om de randen wikkelt. Kristalheldere of bedrukte designs met anti-vingerafdruk coating.",
    hydrogelFeature1: "Zelfherstellende technologie",
    hydrogelFeature2: "0,15mm ultradun",
    hydrogelFeature3: "Anti-vingerafdruk coating",
    hydrogelFeature4: "Flexibele randverpakking",
    hydrogelPrice: "Vanaf €14,99",
    leather3m: "3M Leer",
    leather3mTag: "Premium Textuur",
    leather3mDesc: "Authentiek 3M getextureerd vinyl met rijke leernerf. Een luxueus tastbaar gevoel dat je apparaat transformeert.",
    leather3mFeature1: "Authentiek 3M vinyl",
    leather3mFeature2: "Rijke leertextuur",
    leather3mFeature3: "UV- & krasbestendig",
    leather3mFeature4: "Precisie lasergesneden",
    leather3mPrice: "Vanaf €19,99",
    comparisonTitle: "Naast elkaar",
    compThickness: "Dikte",
    compThicknessHydro: "0,15mm",
    compThicknessLeather: "0,25mm",
    compTexture: "Textuur",
    compTextureHydro: "Glad / Glanzend",
    compTextureLeather: "Rijke leernerf",
    compProtection: "Bescherming",
    compProtectionHydro: "Zelfherstellende film",
    compProtectionLeather: "3M krasschild",
    compDurability: "Duurzaamheid",
    compDurabilityHydro: "2–3 jaar",
    compDurabilityLeather: "3–5 jaar",
    compApplication: "Toepassing",
    compApplicationHydro: "Natte toepassing, geen bubbels",
    compApplicationLeather: "Droge toepassing, luchtafvoer",
    compBestFor: "Best voor",
    compBestForHydro: "Strak, minimaal uiterlijk",
    compBestForLeather: "Luxe, tastbaar gevoel",
    exploreSkins: "Ontdek skins",
    feature1Title: "Twee premium materialen",
    feature1Desc: "Kies Hydrogel voor ultradunne helderheid of 3M Leer voor luxueuze textuur.",
    feature2Title: "Geen extra dikte",
    feature2Desc: "Ultradunne skins die geen dikte toevoegen aan je apparaat.",
    feature3Title: "Bubbelvrije toepassing",
    feature3Desc: "Luchtafvoertechnologie voor een vlekkeloos, professioneel resultaat.",
    howItWorks: "Hoe het werkt",
    howItWorksDesc: "Drie eenvoudige stappen om je gear te beschermen.",
    step1Title: "Kies je apparaat",
    step1Desc: "Selecteer het merk en model uit onze catalogus.",
    step2Title: "Kies je materiaal",
    step2Desc: "Kies tussen Hydrogel of 3M Leer, dan je design.",
    step3Title: "Breng aan & Geniet",
    step3Desc: "Eenvoudige bubbelvrije toepassing in enkele minuten.",
    cameras: "Camera's",
    iphones: "Telefoons",
    macbooks: "MacBooks",
    controllers: "Controllers",
    accessories: "Accessoires",
    cardSkins: "Kaartskins",
    laptops: "Laptops",
    tablets: "Tablets",
    all: "Alles",
    allProducts: "Alle producten",
    allModels: "Alle modellen",
    skinsSuffix: "Skins",
    comingSoon: "Binnenkort beschikbaar",
    comingSoonDesc: "Deze collectie komt eraan. Kom snel terug — we maken iets bijzonders.",
    categoryHeroDesc: "Hydrogel en 3M Leer skins, op maat gesneden voor elk model.",
    noProducts: "Geen producten gevonden",
    noProductsHint: "Nieuwe producten worden voor deze categorie voorbereid. Kom binnenkort terug.",
    home: "Home",
    brands: "Producten",
    quickLinks: "Snelkoppelingen",
    shop: "Winkel",
    discover: "Ontdek",
    help: "Hulp & Account",
    quizTitle: "Stijl Matchmaker",
    tutorials: "Applicatiegids",
    returns: "Retouren & Terugbetalingen",
    searchPlaceholder: "Zoeken...",
    selectModel: "Selecteer een model om beschikbare skins te zien",
    skinsFor: "Skins voor",
    footerRights: "Alle rechten voorbehouden.",
    footerAboutDesc: "Premium Hydrogel & 3M Leer skins voor camera's, telefoons en laptops. Bescherm en personaliseer je apparaten.",
    newsletterTitle: "Blijf op de hoogte",
    newsletterDesc: "Ontvang exclusieve aanbiedingen en nieuwe skin alerts.",
    newsletterPlaceholder: "Je e-mailadres",
    newsletterButton: "Abonneren",
    backToStore: "Terug naar winkel",
    productNotFound: "Product niet gevonden",
    variant: "Variant",
    addScreenProtector: "Schermbeschermer toevoegen",
    screenProtectorDesc: "9H gehard glas, anti-vingerafdruk",
    addToCart: "In winkelwagen",
    addToCartProtector: "In winkelwagen + Beschermer",
    chooseModel: "Kies model",
    addedToCart: "toegevoegd aan winkelwagen",
    noImage: "Geen afbeelding",
    shoppingCart: "Winkelwagen",
    cartEmpty: "Je winkelwagen is leeg",
    cartAddError: "Dit artikel kon niet worden toegevoegd. Probeer het opnieuw.",
    itemsInCart: "artikel(en) in je winkelwagen",
    total: "Totaal",
    checkoutShopify: "Afrekenen met Shopify",
    freeShipping: "Gratis levering in België vanaf €30",
    freeShippingEU: "Gratis levering in FR, LU & NL vanaf €50",
    shippingDelay: "Verzonden binnen 24–48u",
    satisfiedOrRefunded: "Tevreden of terugbetaald – 30 dagen",
    faqTitle: "Veelgestelde vragen",
    faq1Q: "Wat is het verschil tussen Hydrogel en 3M Leer?",
    faq1A: "Hydrogel is een ultradunne (0,15mm) zelfherstellende film ideaal voor een strak uiterlijk. 3M Leer is een premium getextureerd vinyl (0,25mm) dat je apparaat een luxueus leergevoel geeft.",
    faq2Q: "Hoe lang gaat elk skintype mee?",
    faq2A: "Hydrogel skins gaan 2–3 jaar mee met zelfherstellende eigenschappen. 3M Leer skins zijn nog duurzamer: 3–5 jaar, ontworpen om UV, krassen en slijtage te weerstaan.",
    faq3Q: "Is het aanbrengen moeilijk?",
    faq3A: "Helemaal niet! Hydrogel gebruikt natte toepassing voor perfecte positionering. 3M Leer heeft luchtafvoertechnologie voor bubbelvrije droge toepassing.",
    faq4Q: "Leveren jullie internationaal?",
    faq4A: "Ja, we leveren wereldwijd. Gratis verzending bij bestellingen boven €50 binnen Europa.",
    faq5Q: "Kan ik een skin retourneren of ruilen?",
    faq5A: "Absoluut. We bieden een 30 dagen tevredenheidsgarantie.",
    signIn: "Inloggen",
    createAccount: "Account aanmaken",
    resetPassword: "Wachtwoord resetten",
    signInDesc: "Welkom terug bij SkinVault.",
    signUpDesc: "Word lid van SkinVault en bescherm je gear.",
    resetDesc: "Voer je e-mail in om een resetlink te ontvangen.",
    displayName: "Weergavenaam",
    displayNamePlaceholder: "Je naam",
    emailLabel: "E-mail",
    passwordLabel: "Wachtwoord",
    forgotPassword: "Wachtwoord vergeten?",
    sendResetLink: "Resetlink versturen",
    noAccount: "Nog geen account?",
    haveAccount: "Al een account?",
    checkEmail: "Controleer je e-mail",
    confirmEmail: "We hebben je een bevestigingslink gestuurd. Controleer je e-mail.",
    resetSent: "Resetlink verzonden",
    checkEmailReset: "Controleer je e-mail voor de resetlink.",
    setNewPassword: "Nieuw wachtwoord instellen",
    newPassword: "Nieuw wachtwoord",
    updatePassword: "Bijwerken",
    passwordUpdated: "Wachtwoord succesvol bijgewerkt!",
    invalidResetLink: "Deze resetlink is ongeldig of verlopen.",
    myAccount: "Mijn account",
    signOut: "Uitloggen",
    profileInfo: "Profielinformatie",
    saveProfile: "Opslaan",
    profileSaved: "Profiel opgeslagen!",
    memberSince: "Lid sinds",
    orderHistory: "Bestelgeschiedenis",
    noOrders: "Nog geen bestellingen",
    unfulfilled: "Niet verzonden",
    fulfilled: "Verzonden",
    partiallyFulfilled: "Gedeeltelijk verzonden",
    testimonialsTitle: "Wat onze klanten zeggen",
    testimonialsDesc: "Sluit je aan bij duizenden tevreden klanten die hun gear beschermen met SkinVault.",
    testimonial1Name: "Sophie L.",
    testimonial1Text: "De Hydrogel skin is waanzinnig — nauwelijks zichtbaar maar beschermt perfect. Mijn iPhone ziet er als nieuw uit!",
    testimonial2Name: "Thomas M.",
    testimonial2Text: "Ik ging voor 3M Leer op mijn Canon R5. De textuur is luxueus en ik maak me geen zorgen meer over krassen.",
    testimonial3Name: "Julie V.",
    testimonial3Text: "3M Leer op mijn MacBook krijgt overal complimenten. Het leergevoel is gewoon *fantastisch*!",
    testimonial4Name: "Karim B.",
    testimonial4Text: "Beide types besteld — Hydrogel voor mijn telefoon, 3M Leer voor de camera. Beide uitzonderlijke kwaliteit.",
    bestSellers: "Bestverkocht",
    bestSellersDesc: "Onze populairste skins geliefd door duizenden.",
    whoAreWeTitle: "Wie zijn wij?",
    whoAreWeText: "Wij zijn een klein, gepassioneerd team. We bieden twee premium materialen — Hydrogel voor ultradunne bescherming en 3M Leer voor luxueuze textuur — omdat je apparaten het beste verdienen.",
    newsletterBadge: "-10%",
    newsletterCTATitle: "10% korting op je eerste bestelling",
    newsletterCTADesc: "Schrijf je in voor onze nieuwsbrief en ontvang een exclusieve kortingscode.",
    newsletterSuccess: "Gelukt! Check je inbox voor je code.",
    shippingPageTitle: "Verzending & Retourneren",
    shippingZonesTitle: "Verzendzone's",
    shippingZoneBE: "België",
    shippingZoneBEDetail: "Gratis levering vanaf €30. Standaard verzending: €3,95.",
    shippingZoneEU: "Frankrijk, Luxemburg & Nederland",
    shippingZoneEUDetail: "Gratis levering vanaf €50. Standaard verzending: €5,95.",
    shippingZoneIntl: "Internationaal",
    shippingZoneIntlDetail: "Tarieven berekend bij afrekenen. Meestal €8,95 – €14,95.",
    shippingTimesTitle: "Levertijden",
    shippingTimeProcessing: "Verwerking",
    shippingTimeProcessingDetail: "Bestellingen worden binnen 24 uur op werkdagen verwerkt.",
    shippingTimeDeliveryBE: "Levering België",
    shippingTimeDeliveryBEDetail: "1–2 werkdagen na verzending.",
    shippingTimeDeliveryEU: "Levering EU",
    shippingTimeDeliveryEUDetail: "2–4 werkdagen na verzending.",
    returnsTitle: "Retourneren & Terugbetalingen",
    returnsPolicy: "30 dagen garantie",
    returnsPolicyDetail: "Niet tevreden? Retourneer je ongebruikte skin binnen 30 dagen voor een volledige terugbetaling.",
    returnsProcess: "Hoe retourneren",
    returnsProcessDetail: "Neem contact op via support@skinvault.be en we sturen je een vooraf betaald retourlabel.",
    returnsRefund: "Terugbetalingstermijn",
    returnsRefundDetail: "Terugbetalingen worden binnen 5 werkdagen na ontvangst van je retour verwerkt.",
    shippingPolicyLink: "Verzending & Retourneren",
    chatOnline: "Online — Stel me je vragen!",
    chatWelcome: "Hallo! 👋 Hoe kan ik je helpen?",
    chatPlaceholder: "Typ je vraag...",
    chatError: "Sorry, er ging iets mis. Probeer het opnieuw.",
    quizBadge: "Nieuw",
    quizCTATitle: "Niet zeker welke skin bij je past?",
    quizCTADesc: "Doe onze 1-minuut stijlquiz en krijg een persoonlijke aanbeveling.",
    quizCTAButton: "Doe de Quiz",
    quizQ1: "Naar welke kleuren word je aangetrokken?",
    quizQ1A: "Felle & levendige",
    quizQ1B: "Schone & neutrale",
    quizQ1C: "Warme & aardse",
    quizQ1D: "Unieke & opvallende",
    quizQ2: "Hoe zou je je stijl omschrijven?",
    quizQ2A: "Streetwear / edgy",
    quizQ2B: "Minimalistisch / strak",
    quizQ2C: "Vintage / klassiek",
    quizQ2D: "Artistiek / experimenteel",
    quizQ3: "Welk apparaat wil je eerst skinnen?",
    quizQ3A: "Mijn telefoon",
    quizQ3B: "Mijn laptop",
    quizQ3C: "Mijn camera",
    quizQ3D: "Allemaal!",
    quizQ4: "Welke vibe moet je apparaat uitstralen?",
    quizQ4A: "Opvallen in de menigte",
    quizQ4B: "Strak & professioneel",
    quizQ4C: "Warm & uitnodigend",
    quizQ4D: "Echt een blikvanger",
    quizQ5: "Welk materiaal past het best bij jou?",
    quizQ5A: "Hydrogel — strak & onzichtbaar",
    quizQ5B: "3M Leer — getextureerd & luxueus",
    quizQ5C: "Beide — hangt van het apparaat af",
    quizQ5D: "Nog niet zeker",
    quizBack: "Terug",
    quizYourResult: "Jouw Stijlmatch",
    quizShopNow: "Bekijk mijn picks",
    quizRetake: "Quiz opnieuw doen",
    quizResultBoldTitle: "🔥 De Gewaagde",
    quizResultBoldDesc: "Jij draait om impact. 3M Leer skins met gewaagde texturen passen bij jou.",
    quizResultMinimalTitle: "🤍 De Minimalist",
    quizResultMinimalDesc: "Minder is meer. Hydrogel skins met hun strakke, onzichtbare bescherming passen perfect bij jouw esthetiek.",
    quizResultNaturalTitle: "🌿 De Natuurliefhebber",
    quizResultNaturalDesc: "Je waardeert warmte en authenticiteit. 3M Leer skins brengen dat premium gevoel naar elk apparaat.",
    quizResultCreativeTitle: "✨ De Creatieve Geest",
    quizResultCreativeDesc: "Probeer Hydrogel voor bedrukte designs of 3M Leer voor getextureerde statement pieces.",
    demoLaunchingSoon: "Binnenkort beschikbaar",
    demoHeroTitle1: "Twee materialen,",
    demoHeroTitle2: "oneindig stijl.",
    demoHeroDesc: "Hydrogel voor onzichtbare bescherming. 3M Leer voor luxueuze textuur. Kies je premium materiaal en transformeer elk apparaat.",
    demoDays: "Dagen",
    demoHours: "Uren",
    demoMins: "Min",
    demoSecs: "Sec",
    demoEmailPlaceholder: "Je email voor de lancering 🎁",
    demoNotifyMe: "Breng me op de hoogte",
    demoFirstOrderDiscount: "15% korting op je eerste bestelling. Geen spam.",
    demoSneakPeek: "Sneak Peek",
    demoWhatsDropping: "Wat er aankomt",
    demoUpcomingCollection: "Onze aankomende collectie in Hydrogel & 3M Leer.",
    demoSoon: "Binnenkort",
    demoSeeTheDifference: "Zie het verschil",
    unboxTitle: "De Ervaring",
    unboxHeading1: "Van verpakking",
    unboxHeading2: "tot perfectie",
    unboxStep1: "Premium Materialen",
    unboxStep2: "Verwijder de achterkant",
    unboxStep3: "Breng nauwkeurig aan",
    unboxStep4: "Perfectie bereikt",
    demoDragSlider: "Sleep de schuifregelaar om de transformatie te zien",
    demoWhySkinVault: "Waarom SkinVault?",
    demoEngineered: "Twee premium materialen, ontworpen voor perfectie",
    demoFeature1Title: "Hydrogel Bescherming",
    demoFeature1Desc: "Zelfherstellende, ultradunne film die naadloos om elke rand wikkelt.",
    demoFeature2Title: "3M Leer Luxe",
    demoFeature2Desc: "Authentiek 3M getextureerd vinyl met rijke leernerf voor een premium gevoel.",
    demoFeature3Title: "Bubbelvrije toepassing",
    demoFeature3Desc: "Beide materialen bieden eenvoudige toepassing voor een vlekkeloos resultaat.",
    demoFeature4Title: "Premium Kwaliteit",
    demoFeature4Desc: "Alleen de beste materialen — Hydrogel film en authentiek 3M vinyl.",
    demoStat1: "Skins aangebracht",
    demoStat2: "Premium materialen",
    demoStat3: "Gemiddelde beoordeling",
    demoStat4: "Ultradun",
    demoOurStory: "Ons verhaal",
    demoStory1: "We zijn SkinVault gestart omdat we genoeg hadden van saaie, lompe hoesjes.",
    demoStory2: "We bieden twee premium materialen: Hydrogel voor ultradunne, zelfherstellende bescherming en 3M Leer voor een luxueus getextureerd gevoel.",
    demoStory3: "Van strakke Hydrogel tot rijke 3M Leer — we maken skins voor mensen die willen opvallen.",
    demoDontMissLaunch: "Mis de lancering niet",
    demoJoinWaitlist: "Schrijf je in en krijg 15% korting op je eerste bestelling.",
    demoJoin: "Inschrijven",
    demoInvalidEmail: "Voer een geldig e-mailadres in.",
    demoSignupSuccess: "Je staat op de lijst! 🎉",
    prelaunchCollectionTag: "Collectie preview",
    prelaunchCollectionTitle1: "Ontdek de",
    prelaunchCollectionTitle2: "collectie",
    prelaunchCollectionDesc: "Een selectie premium skins, ontworpen voor al je apparaten.",
    prelaunchGridTitle1: "Elk design,",
    prelaunchGridTitle2: "één visie.",
    prelaunchGridDesc: "Van marmertexturen tot echt leer. Bekijk wat er op je wacht.",
    prelaunchCTATitle1: "Wees de eerste om",
    prelaunchCTATitle2: "de jouwe te krijgen.",
    prelaunchCTADesc: "Schrijf je in en we laten het je weten zodra we live gaan.",
    prelaunchAllRights: "Alle rechten voorbehouden.",
    cfgBadge: "Skin Bouwer",
    cfgTitle: "Ontwerp je perfecte skin",
    cfgDesc: "Kies je apparaat, materiaaltype, design en bekijk het realtime.",
    cfgDevice: "Kies apparaat",
    cfgMaterial: "Materiaaltype",
    cfgSkin: "Kies design",
    cfgFinish: "Kies afwerking",
    cfgOptions: "Opties",
    cfgLogoCutout: "Logo-uitsparing",
    cfgLogoCutoutDesc: "Precisie-uitsparing voor je apparaatlogo (Gratis)",
    cfgSideSkins: "Zijskins",
    cfgSideSkinsDesc: "Voeg beschermende skin toe aan de zijkanten (+€2,00)",
    cfgFront: "Voorkant",
    cfgBack: "Achterkant",
    cfgCompare: "Vergelijken",
    cfgFrontView: "Vooraanzicht",
    cfgBackView: "Achteraanzicht",
    cfgHoverZoom: "Hover om in te zoomen op de textuur",
    cfgSelectSkin: "Selecteer een skin om te bekijken",
    cfgSelectToSee: "Selecteer een apparaat en skin om de prijs te zien",
    cfgIncluded: "Inbegrepen",
    cfgComingSoon: "Binnenkort beschikbaar",
    cfgFreeShipping: "Gratis verzending vanaf €30",
    cfgHydrogel: "Hydrogel",
    cfgLeatherLabel: "3M Leer",
    cfgHydrogelSpecs: "0,15mm · Zelfherstellend",
    cfgLeatherSpecs: "0,25mm · Getextureerde grip",
    cfgFrom: "vanaf",
    cfgCTA: "Bouw je skin",
    cfgAddToCart: "In winkelwagen",
    cfgAdding: "Toevoegen...",
    cfgAdded: "Toegevoegd!",
    cfgAddedToCart: "Toegevoegd aan winkelwagen!",
    cfgWithProduct: "met product",
    galleryTitle1: "Ontworpen om",
    galleryTitle2: "op te vallen.",
    galleryDesc: "Premium Hydrogel & 3M Leer skins gemaakt voor perfectie.",
    skinBuilderLabel: "Skin Bouwer",
    skinBuilderNew: "NIEUW",
    skinBuilderCTATitle1: "Ontwerp je",
    skinBuilderCTATitle2: "Perfecte Skin",
    skinBuilderCTADesc: "Kies tussen Hydrogel of 3M Leer, je design, en bekijk in onze interactieve bouwer.",
    skinBuilderOpen: "Open Skin Bouwer",
    fromPrice: "Vanaf",
    // Tutorial page
    tutorialBadge: "Installatiegidsen",
    tutorialTitle1: "Hoe je je",
    tutorialTitle2: "skin aanbrengt",
    tutorialIntro: "Stapsgewijze videotutorials voor elk product dat we aanbieden — telefoons, tablets, laptops, consoles en accessoires. Kies hieronder je apparaat om te beginnen.",
    tutorialTabPhone: "Telefoon",
    tutorialTabTablet: "Tablet",
    tutorialTabLaptop: "Laptop / PC",
    tutorialTabConsole: "Console",
    tutorialTabAccessories: "Accessoires",
    tutorialDifficultyEasy: "Makkelijk",
    tutorialDifficultyMedium: "Gemiddeld",
    tutorialDifficultyAdvanced: "Gevorderd",
    tutorialWatchInstall: "Bekijk installatie →",
    tutorialWatchAria: "Bekijk installatie op YouTube",
    tutorialNeedHelpTitle: "Meer hulp nodig?",
    tutorialNeedHelpDesc: "Geen tutorial voor jouw apparaat gevonden? Ons supportteam helpt je graag stap voor stap met de installatie.",
    tutorialContactSupport: "Contacteer support",
    tutorialCatBackProtectionLabel: "Achterbescherming",
    tutorialCatBackProtectionDesc: "Hydrogel achterbeschermer — beschermt je apparaat tegen krassen en schaafplekken.",
    tutorialCatBackSkinLabel: "Achterskin",
    tutorialCatBackSkinDesc: "Premium 3M leer en vinyl achterskins voor een luxe afwerking.",
    tutorialCatScreenProtectionLabel: "Schermbescherming",
    tutorialCatScreenProtectionDesc: "Zelfherstellende hydrogel screenprotector — volledige rand-tot-rand dekking.",
    tutorialCatAllAroundLabel: "Volledige telefoonskin",
    tutorialCatAllAroundDesc: "Volledige bedekking: achterkant, zijkanten, randen en cameraring.",
    tutorialCatTabletBackLabel: "Tablet achterskin",
    tutorialCatTabletBackDesc: "Bedek de achterkant van je iPad of Galaxy Tab met premium materialen.",
    tutorialCatTabletScreenLabel: "Tablet schermbescherming",
    tutorialCatTabletScreenDesc: "Papier-gevoel en hydrogel screenprotectors voor tablets.",
    tutorialCatLaptopLabel: "Laptopskin",
    tutorialCatLaptopDesc: "MacBook- en laptopskins — deksel, palmsteun en onderpaneel.",
    tutorialCatConsoleLabel: "Console- & controllerskins",
    tutorialCatConsoleDesc: "PlayStation, Xbox en Nintendo Switch — volledige sets en controller wraps.",
    tutorialCatAccessoriesLabel: "Koptelefoons & accessoires",
    tutorialCatAccessoriesDesc: "AirPods cases, koptelefoonkussens en andere kleine accessoires.",
    trackOrderLink: "Volg je bestelling",
    trackBadge: "Bestelling volgen",
    trackTitle: "Volg je bestelling",
    trackSubtitle: "Voer je trackingnummer in om de laatste leveringsupdates te zien.",
    trackPlaceholder: "bijv. LX123456789FR",
    trackButton: "Volgen",
    trackNumberLabel: "Trackingnummer",
    trackCourier: "Vervoerder",
    trackHistory: "Volggeschiedenis",
    trackNoEvents: "Nog geen trackinggegevens. Probeer het later opnieuw.",
    trackError: "Kan trackinginformatie niet ophalen.",
    trackStatusDelivered: "Bezorgd",
    trackStatusInTransit: "Onderweg",
    trackStatusPending: "In afwachting",
    trackStatusException: "Probleem",
    trackStatusUnknown: "Onbekend",
  },
  pl: {
    heroTag: "Skórki Premium na Urządzenia",
    heroPhoneTitle1: "Chroń swój",
    heroPhoneTitle2: "telefon ze stylem.",
    heroPhoneDesc: "Wybierz między Hydrogel a Skórą 3M dla iPhone i Samsung. Dwa materiały premium, jedno idealne dopasowanie.",
    heroCameraTitle1: "Podnieś poziom",
    heroCameraTitle2: "swojego aparatu.",
    heroCameraDesc: "Skórki Hydrogel lub Skóra 3M dla Canon i Nikon. Chroń korpus aparatu stylem, który wyróżnia się.",
    heroMacbookTitle1: "Odmień wygląd",
    heroMacbookTitle2: "swojego MacBooka.",
    heroMacbookDesc: "Skórki Hydrogel lub Skóra 3M dla MacBook Pro i Air. Spraw, by Twój laptop był jedyny w swoim rodzaju.",
    heroControllerTitle1: "Ulepsz styl",
    heroControllerTitle2: "swojego kontrolera.",
    heroControllerDesc: "Skórki Hydrogel na kontrolery PS5 DualSense. 6 ekskluzywnych wzorów do Twojego zestawu gamingowego.",
    heroTelephoneTitle1: "Styl dla Twojego",
    heroTelephoneTitle2: "telefonu.",
    heroTelephoneDesc: "Skórki Hydrogel i Skóra 3M dla najnowszych iPhone, Samsung i innych.",
    heroComputerTitle1: "Ulepsz swój",
    heroComputerTitle2: "komputer.",
    heroComputerDesc: "Skórki Winyl 3M dla MacBook, Dell, Lenovo i innych popularnych laptopów.",
    heroConsoleTitle1: "Spersonalizuj swoją",
    heroConsoleTitle2: "konsolę.",
    heroConsoleDesc: "Skórki Winyl 3M dla PS5, PS4, Xbox i Nintendo Switch.",
    heroCameraPhotoTitle1: "Chroń swój",
    heroCameraPhotoTitle2: "aparat.",
    heroCameraPhotoDesc: "Skórki Winyl 3M dla aparatów i obiektywów Canon, Nikon i Sony.",
    telephones: "Telefony",
    computers: "Komputery",
    consoles: "Konsole",
    cameraPhoto: "Aparat fotograficzny",
    shopNow: "Kup teraz",
    dragToCompare: "← Przeciągnij, aby porównać →",
    before: "Przed",
    withSkin: "Ze skórką",
    twoTypesTag: "Dwa Materiały Premium",
    twoTypesTitle1: "Wybierz swój",
    twoTypesTitle2: "Materiał",
    twoTypesDesc: "Każde urządzenie zasługuje na idealną skórkę. Wybierz między naszymi dwoma materiałami premium.",
    hydrogel: "Hydrogel",
    hydrogelTag: "Ultracienka Ochrona",
    hydrogelDesc: "Samoregenerujący się, ultra-elastyczny film, który owija się wokół krawędzi. Krystalicznie czyste lub drukowane wzory z powłoką antyodciskową.",
    hydrogelFeature1: "Technologia samoregeneracji",
    hydrogelFeature2: "0,15mm ultracienki",
    hydrogelFeature3: "Powłoka antyodciskowa",
    hydrogelFeature4: "Elastyczne owijanie krawędzi",
    hydrogelPrice: "Od 14,99 €",
    leather3m: "Skóra 3M",
    leather3mTag: "Premium Tekstura",
    leather3mDesc: "Autentyczny winyl 3M z bogatym wzorem skórzanym. Luksusowy dotyk, który przekształca Twoje urządzenie w wyjątkowy przedmiot.",
    leather3mFeature1: "Autentyczny winyl 3M",
    leather3mFeature2: "Bogata tekstura skóry",
    leather3mFeature3: "Odporny na UV i zarysowania",
    leather3mFeature4: "Precyzyjne cięcie laserowe",
    leather3mPrice: "Od 19,99 €",
    comparisonTitle: "Porównanie",
    compThickness: "Grubość",
    compThicknessHydro: "0,15mm",
    compThicknessLeather: "0,25mm",
    compTexture: "Tekstura",
    compTextureHydro: "Gładka / Błyszcząca",
    compTextureLeather: "Bogaty wzór skóry",
    compProtection: "Ochrona",
    compProtectionHydro: "Film samoregenerujący",
    compProtectionLeather: "Tarcza 3M antyrysy",
    compDurability: "Trwałość",
    compDurabilityHydro: "2–3 lata",
    compDurabilityLeather: "3–5 lat",
    compApplication: "Aplikacja",
    compApplicationHydro: "Na mokro, bez bąbelków",
    compApplicationLeather: "Na sucho, odprowadzanie powietrza",
    compBestFor: "Najlepszy do",
    compBestForHydro: "Elegancki, minimalny wygląd",
    compBestForLeather: "Luksus, wrażenie dotykowe",
    exploreSkins: "Odkryj skórki",
    feature1Title: "Dwa materiały premium",
    feature1Desc: "Wybierz Hydrogel dla ultracienkiej przejrzystości lub Skórę 3M dla luksusowej tekstury.",
    feature2Title: "Zero grubości",
    feature2Desc: "Ultracienkie skórki, które nie dodają grubości Twojemu urządzeniu.",
    feature3Title: "Aplikacja bez bąbelków",
    feature3Desc: "Technologia odprowadzania powietrza zapewniająca idealne wykończenie.",
    howItWorks: "Jak to działa",
    howItWorksDesc: "Trzy proste kroki, aby chronić swój sprzęt.",
    step1Title: "Wybierz urządzenie",
    step1Desc: "Wybierz markę i model z naszego katalogu.",
    step2Title: "Wybierz materiał",
    step2Desc: "Wybierz między Hydrogel a Skórą 3M, potem design.",
    step3Title: "Naklej i ciesz się",
    step3Desc: "Łatwa aplikacja bez bąbelków w kilka minut.",
    cameras: "Aparaty",
    iphones: "Telefony",
    macbooks: "MacBooki",
    controllers: "Kontrolery",
    accessories: "Akcesoria",
    cardSkins: "Skiny na karty",
    laptops: "Laptopy",
    tablets: "Tablety",
    all: "Wszystko",
    allProducts: "Wszystkie produkty",
    allModels: "Wszystkie modele",
    skinsSuffix: "Skórki",
    comingSoon: "Wkrótce",
    comingSoonDesc: "Ta kolekcja pojawi się wkrótce. Zajrzyj ponownie — przygotowujemy coś wyjątkowego.",
    categoryHeroDesc: "Skórki Hydrogel i Skóra 3M, wycinane na miarę dla każdego modelu.",
    noProducts: "Nie znaleziono produktów",
    noProductsHint: "Nowe produkty są przygotowywane dla tej kategorii. Wróć wkrótce.",
    home: "Strona główna",
    brands: "Produkty",
    quickLinks: "Szybkie linki",
    shop: "Sklep",
    discover: "Odkryj",
    help: "Pomoc i konto",
    quizTitle: "Dobór stylu",
    tutorials: "Przewodnik aplikacji",
    returns: "Zwroty i refundacje",
    searchPlaceholder: "Szukaj...",
    selectModel: "Wybierz model, aby zobaczyć dostępne skórki",
    skinsFor: "Skórki do",
    footerRights: "Wszelkie prawa zastrzeżone.",
    footerAboutDesc: "Skórki Hydrogel & Skóra 3M premium do aparatów, telefonów i laptopów. Chroń i personalizuj swoje urządzenia.",
    newsletterTitle: "Bądź na bieżąco",
    newsletterDesc: "Otrzymuj ekskluzywne oferty i nowości.",
    newsletterPlaceholder: "Twój adres email",
    newsletterButton: "Subskrybuj",
    backToStore: "Powrót do sklepu",
    productNotFound: "Nie znaleziono produktu",
    variant: "Wariant",
    addScreenProtector: "Dodaj ochraniacz ekranu",
    screenProtectorDesc: "Szkło hartowane 9H, antyodciskowe",
    addToCart: "Dodaj do koszyka",
    addToCartProtector: "Dodaj do koszyka + Ochraniacz",
    chooseModel: "Wybierz model",
    addedToCart: "dodano do koszyka",
    noImage: "Brak zdjęcia",
    shoppingCart: "Koszyk",
    cartEmpty: "Twój koszyk jest pusty",
    cartAddError: "Nie udało się dodać produktu. Spróbuj ponownie.",
    itemsInCart: "przedmiot(ów) w koszyku",
    total: "Suma",
    checkoutShopify: "Zapłać przez Shopify",
    freeShipping: "Darmowa dostawa w Belgii od 30 €",
    freeShippingEU: "Darmowa dostawa w FR, LU & NL od 50 €",
    shippingDelay: "Wysyłka w ciągu 24–48h",
    satisfiedOrRefunded: "Zadowolony lub zwrot – 30 dni",
    faqTitle: "Często zadawane pytania",
    faq1Q: "Jaka jest różnica między Hydrogel a Skórą 3M?",
    faq1A: "Hydrogel to ultracienki (0,15mm) samoregenerujący się film idealny do eleganckiego wyglądu. Skóra 3M to premium winyl z teksturą (0,25mm) dający luksusowy dotyk skóry.",
    faq2Q: "Jak długo wytrzymuje każdy typ skórki?",
    faq2A: "Skórki Hydrogel wytrzymują 2–3 lata z właściwościami samoregeneracji. Skórki Skóra 3M są jeszcze trwalsze: 3–5 lat.",
    faq3Q: "Czy aplikacja jest trudna?",
    faq3A: "Wcale nie! Hydrogel stosuje się na mokro. Skóra 3M ma technologię odprowadzania powietrza do aplikacji suchej bez bąbelków.",
    faq4Q: "Czy wysyłacie międzynarodowo?",
    faq4A: "Tak, wysyłamy na cały świat. Darmowa wysyłka przy zamówieniach powyżej 50 € w Europie.",
    faq5Q: "Czy mogę zwrócić lub wymienić skórkę?",
    faq5A: "Oczywiście. Oferujemy 30-dniową gwarancję satysfakcji.",
    signIn: "Zaloguj się",
    createAccount: "Utwórz konto",
    resetPassword: "Zresetuj hasło",
    signInDesc: "Witaj ponownie w SkinVault.",
    signUpDesc: "Dołącz do SkinVault i chroń swój sprzęt.",
    resetDesc: "Wpisz swój email, aby otrzymać link do resetowania.",
    displayName: "Nazwa wyświetlana",
    displayNamePlaceholder: "Twoje imię",
    emailLabel: "Email",
    passwordLabel: "Hasło",
    forgotPassword: "Nie pamiętasz hasła?",
    sendResetLink: "Wyślij link",
    noAccount: "Nie masz konta?",
    haveAccount: "Masz już konto?",
    checkEmail: "Sprawdź email",
    confirmEmail: "Wysłaliśmy link potwierdzający. Sprawdź swoją skrzynkę.",
    resetSent: "Link wysłany",
    checkEmailReset: "Sprawdź email w celu zresetowania hasła.",
    setNewPassword: "Ustaw nowe hasło",
    newPassword: "Nowe hasło",
    updatePassword: "Aktualizuj",
    passwordUpdated: "Hasło zaktualizowane!",
    invalidResetLink: "Ten link jest nieprawidłowy lub wygasł.",
    myAccount: "Moje konto",
    signOut: "Wyloguj się",
    profileInfo: "Informacje o profilu",
    saveProfile: "Zapisz",
    profileSaved: "Profil zapisany!",
    memberSince: "Członek od",
    orderHistory: "Historia zamówień",
    noOrders: "Brak zamówień",
    unfulfilled: "Niewykonane",
    fulfilled: "Zrealizowane",
    partiallyFulfilled: "Częściowo zrealizowane",
    testimonialsTitle: "Co mówią nasi klienci",
    testimonialsDesc: "Dołącz do tysięcy zadowolonych klientów.",
    testimonial1Name: "Sophie L.",
    testimonial1Text: "Skórka Hydrogel jest niesamowita — ledwo widoczna, ale chroni idealnie. Mój iPhone wygląda jak nowy!",
    testimonial2Name: "Thomas M.",
    testimonial2Text: "Wybrałem Skórę 3M na mojego Canon R5. Tekstura jest luksusowa i nie martwię się o zarysowania.",
    testimonial3Name: "Julie V.",
    testimonial3Text: "Skóra 3M na moim MacBooku zbiera komplementy wszędzie. Faktura skóry jest po prostu *perfekcyjna*!",
    testimonial4Name: "Karim B.",
    testimonial4Text: "Zamówiłem oba typy — Hydrogel na telefon, Skóra 3M na aparat. Oba są wyjątkowej jakości.",
    bestSellers: "Bestsellery",
    bestSellersDesc: "Nasze najpopularniejsze skórki.",
    whoAreWeTitle: "Kim jesteśmy?",
    whoAreWeText: "Jesteśmy małym, pełnym pasji zespołem. Oferujemy dwa materiały premium — Hydrogel i Skórę 3M — bo Twoje urządzenia zasługują na to, co najlepsze.",
    newsletterBadge: "-10%",
    newsletterCTATitle: "10% zniżki na pierwsze zamówienie",
    newsletterCTADesc: "Zapisz się do newslettera i otrzymaj ekskluzywny kod rabatowy.",
    newsletterSuccess: "Udało się! Sprawdź skrzynkę po kod.",
    shippingPageTitle: "Wysyłka & Zwroty",
    shippingZonesTitle: "Strefy wysyłki",
    shippingZoneBE: "Belgia",
    shippingZoneBEDetail: "Darmowa dostawa od 30 €. Standardowa: 3,95 €.",
    shippingZoneEU: "Francja, Luksemburg & Holandia",
    shippingZoneEUDetail: "Darmowa dostawa od 50 €. Standardowa: 5,95 €.",
    shippingZoneIntl: "Międzynarodowa",
    shippingZoneIntlDetail: "Stawki naliczane przy kasie. Zwykle 8,95 € – 14,95 €.",
    shippingTimesTitle: "Czasy dostawy",
    shippingTimeProcessing: "Przetwarzanie",
    shippingTimeProcessingDetail: "Zamówienia przetwarzane w 24h w dni robocze.",
    shippingTimeDeliveryBE: "Dostawa Belgia",
    shippingTimeDeliveryBEDetail: "1–2 dni robocze po wysyłce.",
    shippingTimeDeliveryEU: "Dostawa UE",
    shippingTimeDeliveryEUDetail: "2–4 dni robocze po wysyłce.",
    returnsTitle: "Zwroty & Refundacje",
    returnsPolicy: "Gwarancja 30 dni",
    returnsPolicyDetail: "Niezadowolony? Zwróć nieużywaną skórkę w 30 dni.",
    returnsProcess: "Jak zwrócić",
    returnsProcessDetail: "Skontaktuj się pod support@skinvault.be.",
    returnsRefund: "Termin zwrotu",
    returnsRefundDetail: "Refundacje przetwarzane w 5 dni roboczych.",
    shippingPolicyLink: "Wysyłka & Zwroty",
    chatOnline: "Online — Pytaj o co chcesz!",
    chatWelcome: "Cześć! 👋 Jak mogę pomóc?",
    chatPlaceholder: "Wpisz pytanie...",
    chatError: "Przepraszamy, coś poszło nie tak.",
    quizBadge: "Nowość",
    quizCTATitle: "Nie wiesz, która skórka do Ciebie pasuje?",
    quizCTADesc: "Wypełnij nasz 1-minutowy quiz stylu.",
    quizCTAButton: "Zrób Quiz",
    quizQ1: "Jakie kolory Cię przyciągają?",
    quizQ1A: "Odważne & żywe",
    quizQ1B: "Czyste & neutralne",
    quizQ1C: "Ciepłe & ziemiste",
    quizQ1D: "Unikalne & przyciągające wzrok",
    quizQ2: "Jak opisałbyś swój styl?",
    quizQ2A: "Streetwear / edgy",
    quizQ2B: "Minimalistyczny / elegancki",
    quizQ2C: "Vintage / klasyczny",
    quizQ2D: "Artystyczny / eksperymentalny",
    quizQ3: "Które urządzenie chcesz obkleić pierwsze?",
    quizQ3A: "Mój telefon",
    quizQ3B: "Mój laptop",
    quizQ3C: "Mój aparat",
    quizQ3D: "Wszystkie!",
    quizQ4: "Jaką atmosferę powinno dawać Twoje urządzenie?",
    quizQ4A: "Wyróżniać się",
    quizQ4B: "Elegancko & profesjonalnie",
    quizQ4C: "Ciepło & przytulnie",
    quizQ4D: "Przyciągać spojrzenia",
    quizQ5: "Który materiał bardziej do Ciebie pasuje?",
    quizQ5A: "Hydrogel — elegancki & niewidoczny",
    quizQ5B: "Skóra 3M — tekstura & luksus",
    quizQ5C: "Oba — zależy od urządzenia",
    quizQ5D: "Jeszcze nie wiem",
    quizBack: "Wstecz",
    quizYourResult: "Twój Styl",
    quizShopNow: "Zobacz moje propozycje",
    quizRetake: "Powtórz quiz",
    quizResultBoldTitle: "🔥 Odważny",
    quizResultBoldDesc: "Skórki Skóra 3M z odważnymi teksturami są dla Ciebie.",
    quizResultMinimalTitle: "🤍 Minimalista",
    quizResultMinimalDesc: "Skórki Hydrogel z ich elegancką, niewidoczną ochroną idealnie pasują do Twojej estetyki.",
    quizResultNaturalTitle: "🌿 Miłośnik Natury",
    quizResultNaturalDesc: "Skórki Skóra 3M przynoszą premium, organiczne wrażenie do każdego urządzenia.",
    quizResultCreativeTitle: "✨ Kreatywny Duch",
    quizResultCreativeDesc: "Spróbuj Hydrogel na drukowane wzory lub Skóra 3M na teksturowane statement pieces.",
    demoLaunchingSoon: "Wkrótce premiera",
    demoHeroTitle1: "Dwa materiały,",
    demoHeroTitle2: "nieskończony styl.",
    demoHeroDesc: "Hydrogel dla niewidocznej ochrony. Skóra 3M dla luksusowej tekstury. Wybierz materiał premium i odmień swoje urządzenie.",
    demoDays: "Dni",
    demoHours: "Godz.",
    demoMins: "Min",
    demoSecs: "Sek",
    demoEmailPlaceholder: "Twój email na dzień premiery 🎁",
    demoNotifyMe: "Powiadom mnie",
    demoFirstOrderDiscount: "15% zniżki na pierwsze zamówienie. Bez spamu.",
    demoSneakPeek: "Podgląd",
    demoWhatsDropping: "Co nadchodzi",
    demoUpcomingCollection: "Nasza nadchodząca kolekcja w Hydrogel & Skóra 3M.",
    demoSoon: "Wkrótce",
    demoSeeTheDifference: "Zobacz różnicę",
    unboxTitle: "Doświadczenie",
    unboxHeading1: "Od opakowania",
    unboxHeading2: "do perfekcji",
    unboxStep1: "Materiały Premium",
    unboxStep2: "Odklej podkład",
    unboxStep3: "Nałóż precyzyjnie",
    unboxStep4: "Perfekcja osiągnięta",
    demoDragSlider: "Przeciągnij suwak, aby zobaczyć transformację",
    demoWhySkinVault: "Dlaczego SkinVault?",
    demoEngineered: "Dwa materiały premium, zaprojektowane z myślą o perfekcji",
    demoFeature1Title: "Ochrona Hydrogel",
    demoFeature1Desc: "Samoregenerujący się, ultracienki film owijający każdą krawędź.",
    demoFeature2Title: "Luksus Skóry 3M",
    demoFeature2Desc: "Autentyczny winyl 3M z bogatym wzorem skórzanym dla premium wrażenia.",
    demoFeature3Title: "Aplikacja bez bąbelków",
    demoFeature3Desc: "Oba materiały oferują łatwą aplikację dla idealnego wykończenia.",
    demoFeature4Title: "Jakość Premium",
    demoFeature4Desc: "Tylko najlepsze materiały — film Hydrogel i autentyczny winyl 3M.",
    demoStat1: "Nałożonych skórek",
    demoStat2: "Materiały premium",
    demoStat3: "Średnia ocena",
    demoStat4: "Ultracienkie",
    demoOurStory: "Nasza historia",
    demoStory1: "Stworzyliśmy SkinVault, bo mieliśmy dość nudnych etui.",
    demoStory2: "Oferujemy dwa materiały premium: Hydrogel i Skórę 3M — bo Twoje urządzenia zasługują na to, co najlepsze.",
    demoStory3: "Od eleganckiego Hydrogel po bogatą Skórę 3M — tworzymy skórki dla tych, którzy chcą się wyróżniać.",
    demoDontMissLaunch: "Nie przegap startu",
    demoJoinWaitlist: "Zapisz się na listę i otrzymaj 15% zniżki.",
    demoJoin: "Dołącz",
    demoInvalidEmail: "Proszę podać poprawny email.",
    demoSignupSuccess: "Jesteś na liście! 🎉",
    prelaunchCollectionTag: "Podgląd kolekcji",
    prelaunchCollectionTitle1: "Odkryj",
    prelaunchCollectionTitle2: "kolekcję",
    prelaunchCollectionDesc: "Wybór premium skinów, zaprojektowanych dla każdego urządzenia.",
    prelaunchGridTitle1: "Każdy design,",
    prelaunchGridTitle2: "jedna wizja.",
    prelaunchGridDesc: "Od marmurowych tekstur po prawdziwą skórę. Zobacz co na Ciebie czeka.",
    prelaunchCTATitle1: "Bądź pierwszym, który",
    prelaunchCTATitle2: "zdobędzie swój.",
    prelaunchCTADesc: "Zapisz się, a powiadomimy Cię gdy wystartujemy.",
    prelaunchAllRights: "Wszelkie prawa zastrzeżone.",
    cfgBadge: "Kreator Skin",
    cfgTitle: "Zaprojektuj idealny skin",
    cfgDesc: "Wybierz urządzenie, typ materiału, design i podgląd w czasie rzeczywistym.",
    cfgDevice: "Wybierz urządzenie",
    cfgMaterial: "Typ materiału",
    cfgSkin: "Wybierz design",
    cfgFinish: "Wybierz wykończenie",
    cfgOptions: "Opcje",
    cfgLogoCutout: "Wycięcie na logo",
    cfgLogoCutoutDesc: "Precyzyjne wycięcie na logo urządzenia (Bezpłatne)",
    cfgSideSkins: "Skiny boczne",
    cfgSideSkinsDesc: "Dodaj ochronny skin na boki urządzenia (+2,00 €)",
    cfgFront: "Przód",
    cfgBack: "Tył",
    cfgCompare: "Porównaj",
    cfgFrontView: "Widok z przodu",
    cfgBackView: "Widok z tyłu",
    cfgHoverZoom: "Najedź, aby powiększyć teksturę",
    cfgSelectSkin: "Wybierz skin, aby zobaczyć podgląd",
    cfgSelectToSee: "Wybierz urządzenie i skin, aby zobaczyć cenę",
    cfgIncluded: "W cenie",
    cfgComingSoon: "Wkrótce dostępne",
    cfgFreeShipping: "Darmowa dostawa od 30 €",
    cfgHydrogel: "Hydrogel",
    cfgLeatherLabel: "Skóra 3M",
    cfgHydrogelSpecs: "0,15mm · Samoregenerująca",
    cfgLeatherSpecs: "0,25mm · Teksturowany chwyt",
    cfgFrom: "od",
    cfgCTA: "Zaprojektuj skin",
    cfgAddToCart: "Dodaj do koszyka",
    cfgAdding: "Dodawanie...",
    cfgAdded: "Dodano!",
    cfgAddedToCart: "Dodano do koszyka!",
    cfgWithProduct: "z produktem",
    galleryTitle1: "Zaprojektowane, by",
    galleryTitle2: "się wyróżniać.",
    galleryDesc: "Skórki Hydrogel & Skóra 3M premium stworzone z myślą o perfekcji.",
    skinBuilderLabel: "Kreator Skin",
    skinBuilderNew: "NOWOŚĆ",
    skinBuilderCTATitle1: "Zaprojektuj swój",
    skinBuilderCTATitle2: "idealny skin",
    skinBuilderCTADesc: "Wybierz między Hydrogel a Skórą 3M, design i podgląd w naszym interaktywnym kreatorze.",
    skinBuilderOpen: "Otwórz kreator",
    fromPrice: "Od",
    // Tutorial page
    tutorialBadge: "Poradniki montażu",
    tutorialTitle1: "Jak nałożyć swoją",
    tutorialTitle2: "skórkę",
    tutorialIntro: "Wideo poradniki krok po kroku dla każdego oferowanego produktu — telefonów, tabletów, laptopów, konsol i akcesoriów. Wybierz urządzenie poniżej, aby rozpocząć.",
    tutorialTabPhone: "Telefon",
    tutorialTabTablet: "Tablet",
    tutorialTabLaptop: "Laptop / PC",
    tutorialTabConsole: "Konsola",
    tutorialTabAccessories: "Akcesoria",
    tutorialDifficultyEasy: "Łatwy",
    tutorialDifficultyMedium: "Średni",
    tutorialDifficultyAdvanced: "Zaawansowany",
    tutorialWatchInstall: "Zobacz montaż →",
    tutorialWatchAria: "Zobacz montaż na YouTube",
    tutorialNeedHelpTitle: "Potrzebujesz pomocy?",
    tutorialNeedHelpDesc: "Nie znalazłeś poradnika dla swojego urządzenia? Nasz zespół wsparcia chętnie pomoże Ci w montażu.",
    tutorialContactSupport: "Skontaktuj się z nami",
    tutorialCatBackProtectionLabel: "Ochrona tyłu",
    tutorialCatBackProtectionDesc: "Hydrożelowa folia ochronna na tył — chroni urządzenie przed zarysowaniami.",
    tutorialCatBackSkinLabel: "Skórka na tył",
    tutorialCatBackSkinDesc: "Premium skórki na tył ze skóry 3M i winylu dla luksusowego wykończenia.",
    tutorialCatScreenProtectionLabel: "Ochrona ekranu",
    tutorialCatScreenProtectionDesc: "Samonaprawiająca się hydrożelowa folia na ekran — pełne pokrycie od krawędzi do krawędzi.",
    tutorialCatAllAroundLabel: "Pełna skórka na telefon",
    tutorialCatAllAroundDesc: "Pełne pokrycie: tył, boki, krawędzie i pierścień aparatu.",
    tutorialCatTabletBackLabel: "Skórka na tył tabletu",
    tutorialCatTabletBackDesc: "Pokryj tył iPada lub Galaxy Tab premium materiałami.",
    tutorialCatTabletScreenLabel: "Ochrona ekranu tabletu",
    tutorialCatTabletScreenDesc: "Folie hydrożelowe i o efekcie papieru dla tabletów.",
    tutorialCatLaptopLabel: "Skórka na laptop",
    tutorialCatLaptopDesc: "Skórki na MacBook i laptopy — pokrywa, podpora dłoni i panel dolny.",
    tutorialCatConsoleLabel: "Skórki na konsole i kontrolery",
    tutorialCatConsoleDesc: "PlayStation, Xbox i Nintendo Switch — pełne zestawy i obudowy kontrolerów.",
    tutorialCatAccessoriesLabel: "Słuchawki i akcesoria",
    tutorialCatAccessoriesDesc: "Etui AirPods, nauszniki słuchawek i inne drobne akcesoria.",
    trackOrderLink: "Śledź zamówienie",
    trackBadge: "Śledzenie zamówienia",
    trackTitle: "Śledź swoje zamówienie",
    trackSubtitle: "Wprowadź numer śledzenia, aby zobaczyć najnowsze aktualizacje dostawy.",
    trackPlaceholder: "np. LX123456789FR",
    trackButton: "Śledź",
    trackNumberLabel: "Numer śledzenia",
    trackCourier: "Przewoźnik",
    trackHistory: "Historia śledzenia",
    trackNoEvents: "Brak zdarzeń śledzenia. Sprawdź ponownie później.",
    trackError: "Nie można pobrać informacji o śledzeniu.",
    trackStatusDelivered: "Dostarczone",
    trackStatusInTransit: "W drodze",
    trackStatusPending: "Oczekujące",
    trackStatusException: "Wyjątek",
    trackStatusUnknown: "Nieznane",
  },
  ro: {
    heroTag: "Skinuri Premium pentru Dispozitive",
    heroPhoneTitle1: "Protejează-ți",
    heroPhoneTitle2: "telefonul cu stil.",
    heroPhoneDesc: "Alege între Hydrogel sau Piele 3M pentru iPhone și Samsung. Două materiale premium, o potrivire perfectă.",
    heroCameraTitle1: "Ridică nivelul",
    heroCameraTitle2: "camerei tale.",
    heroCameraDesc: "Skinuri Hydrogel sau Piele 3M pentru Canon și Nikon. Protejează-ți camera cu un look care iese în evidență.",
    heroMacbookTitle1: "Transformă aspectul",
    heroMacbookTitle2: "MacBook-ului tău.",
    heroMacbookDesc: "Skinuri Hydrogel sau Piele 3M pentru MacBook Pro și Air. Transformă-ți laptopul într-o piesă unică.",
    heroControllerTitle1: "Îmbunătățește stilul",
    heroControllerTitle2: "controller-ului tău.",
    heroControllerDesc: "Skinuri Hydrogel pentru controlere PS5 DualSense. 6 designuri exclusive pentru setup-ul tău de gaming.",
    heroTelephoneTitle1: "Stil pentru",
    heroTelephoneTitle2: "telefonul tău.",
    heroTelephoneDesc: "Skinuri Hydrogel și Piele 3M pentru cele mai noi iPhone, Samsung și altele.",
    heroComputerTitle1: "Upgradează-ți",
    heroComputerTitle2: "computerul.",
    heroComputerDesc: "Skinuri Vinil 3M pentru MacBook, Dell, Lenovo și alte laptopuri populare.",
    heroConsoleTitle1: "Personalizează-ți",
    heroConsoleTitle2: "consola.",
    heroConsoleDesc: "Skinuri Vinil 3M pentru PS5, PS4, Xbox și Nintendo Switch.",
    heroCameraPhotoTitle1: "Protejează-ți",
    heroCameraPhotoTitle2: "camera.",
    heroCameraPhotoDesc: "Skinuri Vinil 3M pentru camere și obiective Canon, Nikon și Sony.",
    telephones: "Telefoane",
    computers: "Calculatoare",
    consoles: "Console",
    cameraPhoto: "Aparat foto",
    shopNow: "Cumpără acum",
    dragToCompare: "← Trage pentru a compara →",
    before: "Înainte",
    withSkin: "Cu Skin",
    twoTypesTag: "Două Materiale Premium",
    twoTypesTitle1: "Alege-ți",
    twoTypesTitle2: "Materialul",
    twoTypesDesc: "Fiecare dispozitiv merită skinul perfect. Alege între cele două materiale premium ale noastre.",
    hydrogel: "Hydrogel",
    hydrogelTag: "Protecție Ultra-Subțire",
    hydrogelDesc: "Un film auto-vindecător, ultra-flexibil care se înfășoară perfect pe margini. Design-uri cristaline sau imprimate cu acoperire anti-amprentă.",
    hydrogelFeature1: "Tehnologie auto-vindecătoare",
    hydrogelFeature2: "0,15mm ultra-subțire",
    hydrogelFeature3: "Acoperire anti-amprentă",
    hydrogelFeature4: "Înfășurare flexibilă pe margini",
    hydrogelPrice: "De la 14,99 €",
    leather3m: "Piele 3M",
    leather3mTag: "Textură Premium",
    leather3mDesc: "Vinil 3M autentic texturaat cu granulație bogată de piele. Un simț tactil luxos care transformă dispozitivul tău.",
    leather3mFeature1: "Vinil 3M autentic",
    leather3mFeature2: "Textură bogată de piele",
    leather3mFeature3: "Rezistent la UV și zgârieturi",
    leather3mFeature4: "Tăietură laser de precizie",
    leather3mPrice: "De la 19,99 €",
    comparisonTitle: "Față în față",
    compThickness: "Grosime",
    compThicknessHydro: "0,15mm",
    compThicknessLeather: "0,25mm",
    compTexture: "Textură",
    compTextureHydro: "Netedă / Lucioasă",
    compTextureLeather: "Granulație bogată de piele",
    compProtection: "Protecție",
    compProtectionHydro: "Film auto-vindecător",
    compProtectionLeather: "Scut anti-zgârieturi 3M",
    compDurability: "Durabilitate",
    compDurabilityHydro: "2–3 ani",
    compDurabilityLeather: "3–5 ani",
    compApplication: "Aplicare",
    compApplicationHydro: "Aplicare umedă, fără bule",
    compApplicationLeather: "Aplicare uscată, eliberare aer",
    compBestFor: "Ideal pentru",
    compBestForHydro: "Aspect elegant, minimal",
    compBestForLeather: "Lux, senzație tactilă",
    exploreSkins: "Explorează skinuri",
    feature1Title: "Două materiale premium",
    feature1Desc: "Alege Hydrogel pentru claritate ultra-subțire sau Piele 3M pentru textură luxoasă.",
    feature2Title: "Zero grosime",
    feature2Desc: "Skinuri ultra-subțiri care nu adaugă grosime dispozitivului tău.",
    feature3Title: "Aplicare fără bule",
    feature3Desc: "Tehnologie de eliberare a aerului pentru un finisaj impecabil.",
    howItWorks: "Cum funcționează",
    howItWorksDesc: "Trei pași simpli pentru a-ți proteja echipamentul.",
    step1Title: "Alege dispozitivul",
    step1Desc: "Selectează marca și modelul din catalogul nostru.",
    step2Title: "Alege materialul",
    step2Desc: "Alege între Hydrogel sau Piele 3M, apoi design-ul tău.",
    step3Title: "Aplică și bucură-te",
    step3Desc: "Aplicare ușoară, fără bule, în câteva minute.",
    cameras: "Camere",
    iphones: "Telefoane",
    macbooks: "MacBook-uri",
    controllers: "Controllere",
    accessories: "Accesorii",
    cardSkins: "Skin-uri pentru carduri",
    laptops: "Laptopuri",
    tablets: "Tablete",
    all: "Toate",
    allProducts: "Toate produsele",
    allModels: "Toate modelele",
    skinsSuffix: "Skinuri",
    comingSoon: "În curând",
    comingSoonDesc: "Această colecție va fi lansată în curând. Revino în scurt timp — pregătim ceva special.",
    categoryHeroDesc: "Skinuri Hydrogel și Piele 3M, decupate la comandă pentru fiecare model.",
    noProducts: "Nu s-au găsit produse",
    noProductsHint: "Pregătim produse noi pentru această categorie. Revino în curând.",
    home: "Acasă",
    brands: "Produse",
    quickLinks: "Linkuri rapide",
    shop: "Magazin",
    discover: "Descoperă",
    help: "Ajutor și cont",
    quizTitle: "Potrivire stil",
    tutorials: "Ghid de aplicare",
    returns: "Retururi și rambursări",
    searchPlaceholder: "Caută...",
    selectModel: "Selectează un model pentru a vedea skinurile disponibile",
    skinsFor: "Skinuri pentru",
    footerRights: "Toate drepturile rezervate.",
    footerAboutDesc: "Skinuri Hydrogel & Piele 3M premium pentru camere, telefoane și laptopuri. Protejează și personalizează-ți dispozitivele.",
    newsletterTitle: "Rămâi la curent",
    newsletterDesc: "Primește oferte exclusive și noutăți.",
    newsletterPlaceholder: "Adresa ta de email",
    newsletterButton: "Abonează-te",
    backToStore: "Înapoi la magazin",
    productNotFound: "Produs negăsit",
    variant: "Variantă",
    addScreenProtector: "Adaugă folie de protecție",
    screenProtectorDesc: "Sticlă călită 9H, anti-amprentă",
    addToCart: "Adaugă în coș",
    addToCartProtector: "Adaugă în coș + Folie",
    chooseModel: "Alege modelul",
    addedToCart: "adăugat în coș",
    noImage: "Fără imagine",
    shoppingCart: "Coș de cumpărături",
    cartEmpty: "Coșul tău este gol",
    cartAddError: "Produsul nu a putut fi adăugat. Încearcă din nou.",
    itemsInCart: "articol(e) în coș",
    total: "Total",
    checkoutShopify: "Plătește cu Shopify",
    freeShipping: "Livrare gratuită în Belgia de la 30 €",
    freeShippingEU: "Livrare gratuită în FR, LU & NL de la 50 €",
    shippingDelay: "Expediat în 24–48h",
    satisfiedOrRefunded: "Satisfăcut sau rambursat – 30 zile",
    faqTitle: "Întrebări frecvente",
    faq1Q: "Care e diferența între Hydrogel și Piele 3M?",
    faq1A: "Hydrogel e un film ultra-subțire (0,15mm) auto-vindecător ideal pentru un aspect elegant. Piele 3M e un vinil premium texturat (0,25mm) care dă un simț luxos de piele.",
    faq2Q: "Cât durează fiecare tip de skin?",
    faq2A: "Skinurile Hydrogel durează 2-3 ani. Skinurile Piele 3M sunt și mai durabile: 3-5 ani.",
    faq3Q: "Este greu de aplicat?",
    faq3A: "Deloc! Hydrogel se aplică umed. Piele 3M are tehnologie de eliberare a aerului pentru aplicare uscată fără bule.",
    faq4Q: "Livrați internațional?",
    faq4A: "Da, livrăm în toată lumea. Livrare gratuită pentru comenzi peste 50 € în Europa.",
    faq5Q: "Pot returna sau schimba un skin?",
    faq5A: "Absolut. Oferim garanție de satisfacție de 30 de zile.",
    signIn: "Conectare",
    createAccount: "Creează cont",
    resetPassword: "Resetare parolă",
    signInDesc: "Bine ai revenit la SkinVault.",
    signUpDesc: "Alătură-te SkinVault și protejează-ți echipamentul.",
    resetDesc: "Introdu emailul pentru a primi un link de resetare.",
    displayName: "Nume afișat",
    displayNamePlaceholder: "Numele tău",
    emailLabel: "Email",
    passwordLabel: "Parolă",
    forgotPassword: "Ai uitat parola?",
    sendResetLink: "Trimite linkul",
    noAccount: "Nu ai cont?",
    haveAccount: "Ai deja un cont?",
    checkEmail: "Verifică emailul",
    confirmEmail: "Ți-am trimis un link de confirmare. Verifică emailul.",
    resetSent: "Link trimis",
    checkEmailReset: "Verifică emailul pentru linkul de resetare.",
    setNewPassword: "Setează parola nouă",
    newPassword: "Parolă nouă",
    updatePassword: "Actualizează",
    passwordUpdated: "Parolă actualizată cu succes!",
    invalidResetLink: "Acest link este invalid sau a expirat.",
    myAccount: "Contul meu",
    signOut: "Deconectare",
    profileInfo: "Informații profil",
    saveProfile: "Salvează",
    profileSaved: "Profil salvat!",
    memberSince: "Membru din",
    orderHistory: "Istoric comenzi",
    noOrders: "Nicio comandă încă",
    unfulfilled: "Neexpediat",
    fulfilled: "Expediat",
    partiallyFulfilled: "Parțial expediat",
    testimonialsTitle: "Ce spun clienții noștri",
    testimonialsDesc: "Alătură-te miilor de clienți mulțumiți.",
    testimonial1Name: "Sophie L.",
    testimonial1Text: "Skinul Hydrogel e incredibil — abia vizibil dar protejează perfect. iPhone-ul meu arată ca nou!",
    testimonial2Name: "Thomas M.",
    testimonial2Text: "Am ales Piele 3M pentru Canon R5. Textura e luxoasă și nu mă mai îngrijorez de zgârieturi.",
    testimonial3Name: "Julie V.",
    testimonial3Text: "Piele 3M pe MacBook-ul meu primește complimente peste tot. Senzația de piele e pur și simplu *perfectă*!",
    testimonial4Name: "Karim B.",
    testimonial4Text: "Am comandat ambele — Hydrogel pentru telefon, Piele 3M pentru cameră. Ambele sunt de calitate excepțională.",
    bestSellers: "Cele mai vândute",
    bestSellersDesc: "Cele mai populare skinuri ale noastre.",
    whoAreWeTitle: "Cine suntem?",
    whoAreWeText: "Suntem o echipă mică și pasionată. Oferim două materiale premium — Hydrogel și Piele 3M — pentru că dispozitivele tale merită ce e mai bun.",
    newsletterBadge: "-10%",
    newsletterCTATitle: "10% reducere la prima comandă",
    newsletterCTADesc: "Abonează-te la newsletter și primește un cod de reducere exclusiv.",
    newsletterSuccess: "Gata! Verifică inbox-ul pentru codul tău.",
    shippingPageTitle: "Livrare și retururi",
    shippingZonesTitle: "Zone de livrare",
    shippingZoneBE: "Belgia",
    shippingZoneBEDetail: "Livrare gratuită de la 30 €. Standard: 3,95 €.",
    shippingZoneEU: "Franța, Luxemburg și Olanda",
    shippingZoneEUDetail: "Livrare gratuită de la 50 €. Standard: 5,95 €.",
    shippingZoneIntl: "Internațional",
    shippingZoneIntlDetail: "Tarife calculate la finalizare. De obicei 8,95 € – 14,95 €.",
    shippingTimesTitle: "Timpuri de livrare",
    shippingTimeProcessing: "Procesare",
    shippingTimeProcessingDetail: "Comenzile sunt procesate în 24h în zilele lucrătoare.",
    shippingTimeDeliveryBE: "Livrare Belgia",
    shippingTimeDeliveryBEDetail: "1–2 zile lucrătoare după expediere.",
    shippingTimeDeliveryEU: "Livrare UE",
    shippingTimeDeliveryEUDetail: "2–4 zile lucrătoare după expediere.",
    returnsTitle: "Retururi și rambursări",
    returnsPolicy: "Garanție 30 zile",
    returnsPolicyDetail: "Nu ești mulțumit? Returnează skinul nefolosit în 30 de zile.",
    returnsProcess: "Cum returnezi",
    returnsProcessDetail: "Contactează-ne la support@skinvault.be.",
    returnsRefund: "Termen de rambursare",
    returnsRefundDetail: "Rambursările sunt procesate în 5 zile lucrătoare.",
    shippingPolicyLink: "Livrare și retururi",
    chatOnline: "Online — Întreabă-mă orice!",
    chatWelcome: "Bună! 👋 Cum te pot ajuta?",
    chatPlaceholder: "Scrie întrebarea ta...",
    chatError: "Ne pare rău, ceva nu a funcționat.",
    quizBadge: "Nou",
    quizCTATitle: "Nu știi ce skin ți se potrivește?",
    quizCTADesc: "Completează quizul nostru de stil de 1 minut.",
    quizCTAButton: "Fă Quizul",
    quizQ1: "Ce culori te atrag?",
    quizQ1A: "Îndrăznețe și vibrante",
    quizQ1B: "Curate și neutre",
    quizQ1C: "Calde și pământii",
    quizQ1D: "Unice și atrăgătoare",
    quizQ2: "Cum ai descrie stilul tău?",
    quizQ2A: "Streetwear / edgy",
    quizQ2B: "Minimalist / elegant",
    quizQ2C: "Vintage / clasic",
    quizQ2D: "Artistic / experimental",
    quizQ3: "Ce dispozitiv vrei să îl skinezi primul?",
    quizQ3A: "Telefonul meu",
    quizQ3B: "Laptopul meu",
    quizQ3C: "Camera mea",
    quizQ3D: "Toate!",
    quizQ4: "Ce vibrație ar trebui să emane dispozitivul tău?",
    quizQ4A: "Să iasă în evidență",
    quizQ4B: "Elegant și profesional",
    quizQ4C: "Cald și primitor",
    quizQ4D: "Un magnet de priviri",
    quizQ5: "Ce material ți se potrivește mai bine?",
    quizQ5A: "Hydrogel — elegant & invizibil",
    quizQ5B: "Piele 3M — texturată & luxoasă",
    quizQ5C: "Ambele — depinde de dispozitiv",
    quizQ5D: "Nu sunt sigur(ă) încă",
    quizBack: "Înapoi",
    quizYourResult: "Stilul tău",
    quizShopNow: "Vezi recomandările mele",
    quizRetake: "Reia quizul",
    quizResultBoldTitle: "🔥 Îndrăzneț(ă)",
    quizResultBoldDesc: "Skinurile Piele 3M cu texturi îndrăznețe sunt potrivite pentru tine.",
    quizResultMinimalTitle: "🤍 Minimalist(ă)",
    quizResultMinimalDesc: "Skinurile Hydrogel cu protecția lor elegantă și invizibilă se potrivesc perfect esteticii tale.",
    quizResultNaturalTitle: "🌿 Iubitor(oare) de natură",
    quizResultNaturalDesc: "Skinurile Piele 3M aduc acea senzație premium și organică fiecărui dispozitiv.",
    quizResultCreativeTitle: "✨ Spirit creativ",
    quizResultCreativeDesc: "Încearcă Hydrogel pentru design-uri imprimate sau Piele 3M pentru piese texturate.",
    demoLaunchingSoon: "Lansare în curând",
    demoHeroTitle1: "Două materiale,",
    demoHeroTitle2: "stil infinit.",
    demoHeroDesc: "Hydrogel pentru protecție invizibilă. Piele 3M pentru textură luxoasă. Alege materialul tău premium și transformă orice dispozitiv.",
    demoDays: "Zile",
    demoHours: "Ore",
    demoMins: "Min",
    demoSecs: "Sec",
    demoEmailPlaceholder: "Emailul tău pentru ziua lansării 🎁",
    demoNotifyMe: "Anunță-mă",
    demoFirstOrderDiscount: "15% reducere la prima comandă. Fără spam.",
    demoSneakPeek: "Previzualizare",
    demoWhatsDropping: "Ce urmează",
    demoUpcomingCollection: "Colecția noastră viitoare în Hydrogel & Piele 3M.",
    demoSoon: "În curând",
    demoSeeTheDifference: "Vezi diferența",
    unboxTitle: "Experiența",
    unboxHeading1: "De la ambalaj",
    unboxHeading2: "la perfecțiune",
    unboxStep1: "Materiale Premium",
    unboxStep2: "Dezlipiți suportul",
    unboxStep3: "Aplicați cu precizie",
    unboxStep4: "Perfecțiune atinsă",
    demoDragSlider: "Trage sliderul pentru a dezvălui transformarea",
    demoWhySkinVault: "De ce SkinVault?",
    demoEngineered: "Două materiale premium, proiectate pentru perfecțiune",
    demoFeature1Title: "Protecție Hydrogel",
    demoFeature1Desc: "Film auto-vindecător, ultra-subțire care se înfășoară perfect pe margini.",
    demoFeature2Title: "Lux Piele 3M",
    demoFeature2Desc: "Vinil 3M autentic texturat cu granulație bogată de piele pentru un simț premium.",
    demoFeature3Title: "Aplicare fără bule",
    demoFeature3Desc: "Ambele materiale oferă aplicare ușoară pentru un rezultat impecabil.",
    demoFeature4Title: "Calitate Premium",
    demoFeature4Desc: "Doar cele mai bune materiale — film Hydrogel și vinil 3M autentic.",
    demoStat1: "Skinuri aplicate",
    demoStat2: "Materiale premium",
    demoStat3: "Rating mediu",
    demoStat4: "Ultra-subțire",
    demoOurStory: "Povestea noastră",
    demoStory1: "Am creat SkinVault pentru că ne-am săturat de husele plictisitoare.",
    demoStory2: "Oferim două materiale premium: Hydrogel și Piele 3M — pentru că dispozitivele tale merită ce e mai bun.",
    demoStory3: "De la Hydrogel elegant la Piele 3M bogată — creăm skinuri pentru cei care vor să iasă în evidență.",
    demoDontMissLaunch: "Nu rata lansarea",
    demoJoinWaitlist: "Înscrie-te pe lista de așteptare și primești 15% reducere.",
    demoJoin: "Alătură-te",
    demoInvalidEmail: "Te rugăm să introduci un email valid.",
    demoSignupSuccess: "Ești pe listă! 🎉",
    prelaunchCollectionTag: "Previzualizare colecție",
    prelaunchCollectionTitle1: "Explorează",
    prelaunchCollectionTitle2: "colecția",
    prelaunchCollectionDesc: "O selecție de skinuri premium, create pentru fiecare dispozitiv.",
    prelaunchGridTitle1: "Fiecare design,",
    prelaunchGridTitle2: "o viziune.",
    prelaunchGridDesc: "De la texturi de marmură la piele autentică. Vezi ce te așteaptă.",
    prelaunchCTATitle1: "Fii primul care",
    prelaunchCTATitle2: "își ia al său.",
    prelaunchCTADesc: "Înscrie-te și te anunțăm când deschidem.",
    prelaunchAllRights: "Toate drepturile rezervate.",
    cfgBadge: "Creator de Skin",
    cfgTitle: "Creează-ți skin-ul perfect",
    cfgDesc: "Alege dispozitivul, tipul de material, design-ul și previzualizează în timp real.",
    cfgDevice: "Alege dispozitivul",
    cfgMaterial: "Tip de material",
    cfgSkin: "Alege design-ul",
    cfgFinish: "Alege finisajul",
    cfgOptions: "Opțiuni",
    cfgLogoCutout: "Decupaj logo",
    cfgLogoCutoutDesc: "Deschidere de precizie pentru logo-ul dispozitivului (Gratuit)",
    cfgSideSkins: "Skinuri laterale",
    cfgSideSkinsDesc: "Adaugă skin protector pe laterale (+2,00 €)",
    cfgFront: "Față",
    cfgBack: "Spate",
    cfgCompare: "Compară",
    cfgFrontView: "Vedere din față",
    cfgBackView: "Vedere din spate",
    cfgHoverZoom: "Treci cu mouse-ul pentru a mări textura",
    cfgSelectSkin: "Selectează un skin pentru previzualizare",
    cfgSelectToSee: "Selectează un dispozitiv și un skin pentru a vedea prețul",
    cfgIncluded: "Inclus",
    cfgComingSoon: "În curând",
    cfgFreeShipping: "Livrare gratuită de la 30 €",
    cfgHydrogel: "Hydrogel",
    cfgLeatherLabel: "Piele 3M",
    cfgHydrogelSpecs: "0,15mm · Auto-vindecabil",
    cfgLeatherSpecs: "0,25mm · Grip texturat",
    cfgFrom: "de la",
    cfgCTA: "Creează-ți skin-ul",
    cfgAddToCart: "Adaugă în coș",
    cfgAdding: "Se adaugă...",
    cfgAdded: "Adăugat!",
    cfgAddedToCart: "Adăugat în coș!",
    cfgWithProduct: "cu produsul",
    galleryTitle1: "Conceput pentru a",
    galleryTitle2: "ieși în evidență.",
    galleryDesc: "Skinuri Hydrogel & Piele 3M premium create pentru perfecțiune.",
    skinBuilderLabel: "Creator de Skin",
    skinBuilderNew: "NOU",
    skinBuilderCTATitle1: "Creează-ți",
    skinBuilderCTATitle2: "Skin-ul Perfect",
    skinBuilderCTADesc: "Alege între Hydrogel sau Piele 3M, design-ul tău, și previzualizează în creatorul nostru interactiv.",
    skinBuilderOpen: "Deschide creatorul",
    fromPrice: "De la",
    // Tutorial page
    tutorialBadge: "Ghiduri de aplicare",
    tutorialTitle1: "Cum să aplici",
    tutorialTitle2: "skin-ul tău",
    tutorialIntro: "Tutoriale video pas cu pas pentru fiecare produs pe care îl oferim — telefoane, tablete, laptopuri, console și accesorii. Alege dispozitivul tău mai jos pentru a începe.",
    tutorialTabPhone: "Telefon",
    tutorialTabTablet: "Tabletă",
    tutorialTabLaptop: "Laptop / PC",
    tutorialTabConsole: "Consolă",
    tutorialTabAccessories: "Accesorii",
    tutorialDifficultyEasy: "Ușor",
    tutorialDifficultyMedium: "Mediu",
    tutorialDifficultyAdvanced: "Avansat",
    tutorialWatchInstall: "Vezi instalarea →",
    tutorialWatchAria: "Vezi instalarea pe YouTube",
    tutorialNeedHelpTitle: "Ai nevoie de ajutor?",
    tutorialNeedHelpDesc: "Nu găsești un tutorial pentru dispozitivul tău? Echipa noastră de suport te ghidează cu plăcere prin procesul de aplicare.",
    tutorialContactSupport: "Contactează suportul",
    tutorialCatBackProtectionLabel: "Protecție spate",
    tutorialCatBackProtectionDesc: "Folie hidrogel pentru spate — protejează dispozitivul de zgârieturi și uzură.",
    tutorialCatBackSkinLabel: "Skin spate",
    tutorialCatBackSkinDesc: "Skinuri premium din piele 3M și vinil pentru un finisaj de lux.",
    tutorialCatScreenProtectionLabel: "Protecție ecran",
    tutorialCatScreenProtectionDesc: "Folie hidrogel autoreparabilă pentru ecran — acoperire completă, margine la margine.",
    tutorialCatAllAroundLabel: "Skin complet telefon",
    tutorialCatAllAroundDesc: "Acoperire totală: spate, laterale, margini și inelul camerei.",
    tutorialCatTabletBackLabel: "Skin spate tabletă",
    tutorialCatTabletBackDesc: "Acoperă spatele iPad-ului sau Galaxy Tab cu materiale premium.",
    tutorialCatTabletScreenLabel: "Protecție ecran tabletă",
    tutorialCatTabletScreenDesc: "Folii hidrogel și cu senzație de hârtie pentru tablete.",
    tutorialCatLaptopLabel: "Skin laptop",
    tutorialCatLaptopDesc: "Skinuri pentru MacBook și laptop — capac, suport palmă și panou inferior.",
    tutorialCatConsoleLabel: "Skinuri console & controllere",
    tutorialCatConsoleDesc: "PlayStation, Xbox și Nintendo Switch — kituri complete și învelișuri pentru controllere.",
    tutorialCatAccessoriesLabel: "Căști & accesorii",
    tutorialCatAccessoriesDesc: "Carcase AirPods, perne pentru căști și alte accesorii mici.",
    trackOrderLink: "Urmărește comanda",
    trackBadge: "Urmărire comandă",
    trackTitle: "Urmărește comanda ta",
    trackSubtitle: "Introdu numărul de urmărire pentru a vedea cele mai recente actualizări de livrare.",
    trackPlaceholder: "ex: LX123456789FR",
    trackButton: "Urmărește",
    trackNumberLabel: "Număr de urmărire",
    trackCourier: "Curier",
    trackHistory: "Istoric urmărire",
    trackNoEvents: "Niciun eveniment de urmărire deocamdată. Verifică din nou mai târziu.",
    trackError: "Nu se pot prelua informațiile de urmărire.",
    trackStatusDelivered: "Livrat",
    trackStatusInTransit: "În tranzit",
    trackStatusPending: "În așteptare",
    trackStatusException: "Excepție",
    trackStatusUnknown: "Necunoscut",
  },
} as const;

type TranslationKey = keyof typeof translations.en;

interface I18nStore {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey) => string;
}

export const useI18n = create<I18nStore>()(
  persist(
    (set, get) => ({
      locale: 'fr',
      setLocale: (locale) => set({ locale }),
      t: (key) => translations[get().locale][key] || key,
    }),
    {
      name: 'skinvault-locale',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({ locale: state.locale }),
    }
  )
);
